import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PublicDumpingMap, { usePublicDumpingReports } from '../components/landing/PublicDumpingMap';
import { computePublicStats, formatDuration, publicStatVisibility, latestReports, describeArea, timeAgo, cleanupDurationText, HOTSPOT_MIN_REPORTS } from '../services/publicDumpingReportsService';
import { PublicMenuDesktop, PublicMenuMobile } from '../components/PublicMenu';
import { PUBLIC_STAT_THRESHOLDS, HOUSEHOLD_APP_URL, BOOKING_CITIES, nearestBookingCity, TRUST_STATS, CITY_CARD_PHOTOS } from '../config/homepage';
import { CITY_CONTENT } from './cityContent';
import { reverseGeocode } from '../utils/geocoding';
import { useAuth } from '../context/AuthContext';
import { TD, FF, TYPE, BTN } from '../theme/brand';
import { QrScanScreen, BookScreen, TrackScreen, ReportFlowScreen } from '../components/landing/AppScreens';
import { useSeo, BUSINESS, CITY_PAGES, WASTE_PICKUP_PATH, organizationJsonLd, localBusinessJsonLd } from '../utils/seo';

/* ─── Brand tokens: src/theme/brand.js ─────────────────────────────────────── */

// A card colour as text on a white card: the brand green and orange are too
// light there, so they switch to their darker partners
const onLight = (c) => ({ [TD.lime]: TD.forest, [TD.orange]: TD.orangeText, '#e07a5f': '#b4532a' }[c] || c);

const SOCIAL_NAMES = { 'fab fa-twitter':'TrashDrop on X (Twitter)', 'fab fa-facebook-f':'TrashDrop on Facebook', 'fab fa-linkedin-in':'TrashDrop on LinkedIn', 'fab fa-instagram':'TrashDrop on Instagram' };

/* ─── Landing-page theme tokens ─────────────────────────────────────────────── */
const LP = {
  dark: {
    pageBg:        '#fff',
    sectionAlt:    TD.cream,
    sectionDark:   TD.ink,
    sectionDark2:  '#0c1210',
    sectionForest: `linear-gradient(160deg,${TD.forestDk} 0%,#0d1a0d 40%,#0a0f0a 100%)`,
    sectionPathway:'#f7f7f5',
    sectionPartner:TD.ink,
    sectionPricing:'#0a0a0a',
    footerBg:      '#050805',
    footerBorder:  'rgba(182,238,105,0.08)',
    navScrollBg:   'rgba(10,15,10,0.96)',
    navScrollBorder:'rgba(182,238,105,0.12)',
    navText:       'rgba(255,255,255,0.75)',
    navBrand:      '#fff',
    mobileMenuBg:  TD.ink,
    heroOverlay:   'linear-gradient(135deg,rgba(10,15,10,0.85) 0%,rgba(10,15,10,0.7) 50%,rgba(10,15,10,0.5) 100%)',
    heroBg:        TD.ink,
    heroText:      '#f0f5f0',
    heroSub:       'rgba(255,255,255,0.72)',
    heroStatLabel: TD.sageOnDark,
    sectionHeading:'#f0f5f0',
    bodyText:      'rgba(255,255,255,0.68)',
    ink2Heading:   TD.ink2,
    sageText:      TD.sage,
    cardBg:        '#fff',
    cardBorder:    'rgba(0,0,0,0.06)',
    stepNum:       'rgba(0,0,0,0.03)',
    darkBoxBg:     TD.ink2,
    darkBoxText:   'rgba(255,255,255,0.72)',
    mapSectionBg:  '#0c1210',
    mapStatBg:     'rgba(255,255,255,0.03)',
    mapStatBorder: 'rgba(255,255,255,0.07)',
    mapStatText:   '#fff',
    mapStatLabel:  'rgba(255,255,255,0.68)',
    pathwayCardBg: '#fff',
    pathwayText:   TD.sage,
    partnerTileBg: 'rgba(255,255,255,0.03)',
    partnerTileB:  'rgba(255,255,255,0.07)',
    partnerH4:     '#f0f5f0',
    partnerP:      'rgba(255,255,255,0.68)',
    pricingBg:     '#0a1a0a',
    pricingText:   'rgba(255,255,255,0.7)',
    subBg:         '#1a1a1a',
    subText:       'rgba(255,255,255,0.72)',
    subSmall:      'rgba(255,255,255,0.65)',
    pricingBarBg:  '#1a1f1a',
    pricingBarText:'rgba(255,255,255,0.65)',
    impactText:    'rgba(255,255,255,0.68)',
    impactCount:   '#fff',
    impactSub:     'rgba(255,255,255,0.65)',
    fabLabel:      TD.ink,
    fabLabelBg:    TD.ink,
    fabLabelColor: '#fff',
    featureBoxBg:  'rgba(182,238,105,0.05)',
    featureBoxBorder:'rgba(182,238,105,0.15)',
    featureTitle:  TD.lime,
    featureIconBg: 'rgba(182,238,105,0.12)',
    featureIcon:   TD.lime,
    featureText:   'rgba(255,255,255,0.72)',
    mapLegendBg:   'rgba(10,15,10,0.9)',
    mapLegendBorder:'rgba(255,255,255,0.1)',
    mapLegendLabel:'rgba(255,255,255,0.68)',
    mapLegendText: 'rgba(255,255,255,0.75)',
    pricingRowBorder:'rgba(255,255,255,0.06)',
    pricingSize:   'rgba(255,255,255,0.65)',
    pricingDesc:   'rgba(255,255,255,0.68)',
    subTierLabel:  'rgba(255,255,255,0.65)',
    connectorBorder:'#fff',
    darkBoxStrong:  '#fff',
    darkBoxFooter:  '#fff',
    darkBoxSub:     TD.sageOnDark,
    howItWorksLabel:TD.sageOnDark,
    sectionLabel:   TD.sageOnDark,
    impactLabel:    `${TD.lime}80`,
    pathwaysLabel:  `${TD.forest}99`,
  },
  light: {
    pageBg:        TD.cream,
    sectionAlt:    TD.cream,
    sectionDark:   '#1b3a28',
    sectionDark2:  TD.cream,
    sectionForest: `linear-gradient(160deg,#e8f5ed 0%,#f0faf2 40%,#fafff8 100%)`,
    sectionPathway:TD.cream,
    sectionPartner:'#1b3a28',
    sectionPricing:'#fafafa',
    footerBg:      '#1b3a28',
    footerBorder:  'rgba(182,238,105,0.15)',
    navScrollBg:   'rgba(255,255,255,0.97)',
    navScrollBorder:'rgba(22,101,52,0.15)',
    navText:       'rgba(27,62,40,0.75)',
    navBrand:      TD.forest,
    mobileMenuBg:  '#fff',
    heroOverlay:   'linear-gradient(135deg,rgba(10,20,12,0.82) 0%,rgba(10,20,12,0.65) 50%,rgba(10,20,12,0.4) 100%)',
    heroBg:        TD.forest,
    heroText:      '#f0f5f0',
    heroSub:       'rgba(255,255,255,0.6)',
    heroStatLabel: '#a7c5b0',
    sectionHeading:TD.ink,
    bodyText:      TD.sage,
    ink2Heading:   TD.ink,
    sageText:      TD.sage,
    cardBg:        '#fff',
    cardBorder:    'rgba(22,101,52,0.1)',
    stepNum:       'rgba(22,101,52,0.04)',
    darkBoxBg:     TD.forest,
    darkBoxText:   'rgba(255,255,255,0.9)',
    mapSectionBg:  TD.cream,
    mapStatBg:     'rgba(22,101,52,0.04)',
    mapStatBorder: 'rgba(22,101,52,0.1)',
    mapStatText:   TD.ink,
    mapStatLabel:  TD.sage,
    pathwayCardBg: '#fff',
    pathwayText:   TD.sage,
    partnerTileBg: 'rgba(255,255,255,0.12)',
    partnerTileB:  'rgba(255,255,255,0.15)',
    partnerH4:     '#f0f5f0',
    partnerP:      'rgba(255,255,255,0.85)',
    pricingBg:     TD.cream,
    pricingText:   TD.sage,
    subBg:         '#fff',
    subText:       TD.sage,
    subSmall:      TD.sage,
    pricingBarBg:  '#e8f5ed',
    pricingBarText:TD.sage,
    impactText:    TD.sage,
    impactCount:   TD.ink,
    impactSub:     TD.sage,
    fabLabel:      '#fff',
    fabLabelBg:    '#1b3a28',
    fabLabelColor: '#fff',
    featureBoxBg:  '#ffffff',
    featureBoxBorder:'rgba(22,101,52,0.18)',
    featureTitle:  TD.forest,
    featureIconBg: 'rgba(22,101,52,0.1)',
    featureIcon:   TD.forest,
    featureText:   TD.sage,
    mapLegendBg:   'rgba(255,255,255,0.95)',
    mapLegendBorder:'rgba(22,101,52,0.15)',
    mapLegendLabel:TD.sage,
    mapLegendText: TD.ink,
    pricingRowBorder:'rgba(22,101,52,0.08)',
    pricingSize:   TD.sage,
    pricingDesc:   TD.sage,
    subTierLabel:  TD.sage,
    connectorBorder:'#f0f9f0',
    darkBoxStrong:  '#f0f5f0',
    darkBoxFooter:  '#f0f5f0',
    darkBoxSub:     '#c8e6cc',
    howItWorksLabel:TD.sage,
    sectionLabel:   TD.sage,
    impactLabel:    TD.forest,
    pathwaysLabel:  TD.forest,
  },
};

/* ─── Landing-page local theme context ─────────────────────────────────────── */
const LpThemeContext = createContext();
const useLpTheme = () => useContext(LpThemeContext);
// `mode` fixes the theme (no toggle on that page); otherwise the visitor's choice is used
const LpThemeProvider = ({ children, mode }) => {
  const [lpMode, setLpMode] = useState(() => mode || localStorage.getItem('td_lp_theme') || 'light');
  const toggleLp = () => setLpMode(m => {
    const next = m === 'dark' ? 'light' : 'dark';
    localStorage.setItem('td_lp_theme', next);
    return next;
  });
  const tokens = LP[lpMode];
  return (
    <LpThemeContext.Provider value={{ lpMode, toggleLp, lp: tokens }}>
      {children}
    </LpThemeContext.Provider>
  );
};

/* ─── Hooks ─────────────────────────────────────────────────────────────────── */
// Sections are readable at rest (in link previews, on slow phones, with
// reduced motion), so nothing waits for a scroll to appear. The hook keeps
// its shape for the sections that use it.
const useReveal = () => {
  const ref = useRef(null);
  return { ref, visible: true };
};

const useCountUp = (end, duration = 2200, shouldStart = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!shouldStart) return;
    let t = null;
    let id;
    const step = (ts) => {
      if (!t) t = ts;
      const p = Math.min((ts - t) / duration, 1);
      setCount(Math.floor(p * end));
      if (p < 1) id = requestAnimationFrame(step);
    };
    id = requestAnimationFrame(step);
    return () => cancelAnimationFrame(id);
  }, [end, duration, shouldStart]);
  return count;
};

/* ═══════════════════════════════════════════════════════════════════════════════
   GLOBAL STYLES
   ═══════════════════════════════════════════════════════════════════════════════ */
const GlobalStyles = () => (
  <style>{`
    @keyframes td-float      { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
    @keyframes td-spin-slow  { to{transform:rotate(360deg)} }
    @keyframes td-blink      { 0%,100%{opacity:1} 50%{opacity:0} }
    @keyframes td-marquee    { to{transform:translateX(-50%)} }
    @keyframes td-fadeup     { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
    @keyframes td-pulse-dot  { 0%,100%{box-shadow:0 0 0 0 rgba(182,238,105,0.4)} 70%{box-shadow:0 0 0 8px rgba(182,238,105,0)} }
    .td-nav-link:hover { color:#b6ee69 !important; }
    .td-pathway-card:hover { transform:translateY(-8px) !important; }
    .td-partner-tile:hover { background:rgba(182,238,105,0.06) !important; border-color:rgba(182,238,105,0.2) !important; }
    .td-social-btn:hover { background:rgba(182,238,105,0.15) !important; border-color:rgba(182,238,105,0.4) !important; color:#b6ee69 !important; }
    .td-footer-link:hover { color:#b6ee69 !important; }
    .td-nav-btn, .td-lp-toggle { white-space:nowrap; }
    @media(max-width:1279px) { .td-lp-toggle-label { display:none; } }
    @media(max-width:1023px) { .td-nav-links{display:none!important} }
    @media(min-width:1024px) { .td-mobile-btn{display:none!important} }
    @media(max-width:768px) { .td-book-qr{display:none!important} .td-app-qr{display:none!important} .td-app-button{display:inline-flex!important} }
    @media(max-width:900px) { .td-hero-grid{grid-template-columns:1fr!important} .td-hero-photo{display:none!important} .td-hero-facts{grid-template-columns:1fr!important; gap:16px!important; margin-top:40px!important} }
    @media(max-width:768px) { .td-services-grid{grid-template-columns:1fr!important} .td-map-stats{grid-template-columns:repeat(2,1fr)!important} .td-impact-grid{grid-template-columns:repeat(2,1fr)!important} }
    @media(max-width:480px) { .td-map-stats{grid-template-columns:1fr!important} .td-impact-grid{grid-template-columns:1fr!important} }
    @media(max-width:800px) { .td-trashwalk{grid-template-columns:1fr!important; gap:28px!important} .td-trashwalk img{max-width:420px; margin:0 auto} }
    @media(max-width:1180px) { .td-pathways-grid{grid-template-columns:repeat(2,1fr)!important; row-gap:32px!important} }
    @media(max-width:900px) { .td-pathways-grid{grid-template-columns:1fr!important} .td-partners-grid{grid-template-columns:1fr!important} }
    @media(max-width:900px) { .td-pricing-grid{grid-template-columns:1fr!important} }
    @media(max-width:900px) { .td-footer-grid{grid-template-columns:1fr 1fr!important} }
    @media(max-width:480px) { .td-footer-grid{grid-template-columns:1fr!important} }
    .td-sticky-book { right:28px; bottom:28px; }
    @media(max-width:768px) {
      .td-sticky-book { left:0; right:0; bottom:0; padding:10px 16px calc(10px + env(safe-area-inset-bottom)); background:rgba(255,255,255,0.96); box-shadow:0 -4px 18px rgba(0,0,0,0.12); }
      .td-sticky-book a { border-radius:12px !important; box-shadow:none !important; }
      .td-page { padding-bottom:76px; }
    }
    @media(max-width:600px) { .td-trust{grid-template-columns:repeat(2,1fr)!important; row-gap:18px!important} .td-city-row{grid-auto-columns:78%!important} }
    .td-city-row { scrollbar-width:thin; }
    .td-lp-toggle { transition: background 0.2s, color 0.2s, border-color 0.2s; }
    .td-lp-toggle:hover { opacity: 0.85; }
  `}</style>
);

/* ═══════════════════════════════════════════════════════════════════════════════
   NAVBAR
   ═══════════════════════════════════════════════════════════════════════════════ */
const HomeNavbar = ({ isAuthenticated }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled]     = useState(false);
  const { lpMode, toggleLp } = useLpTheme();
  const isDark = lpMode === 'dark';
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', fn, { passive:true });
    return () => window.removeEventListener('scroll', fn);
  }, []);
  // Same menu as every other public page (components/PublicMenu.js)
  const navScrollBg     = isDark ? 'rgba(10,15,10,0.96)'        : 'rgba(255,255,255,0.97)';
  const navScrollBorder = isDark ? 'rgba(182,238,105,0.12)'      : 'rgba(22,101,52,0.15)';
  // Light links and theme switch while the bar is see-through over the dark hero
  const overDark        = isDark || !scrolled;
  const navLinkColor    = overDark ? '#f0f5f0' : TD.ink;
  const brandColor      = overDark ? '#fff'                      : TD.forest;
  const mobileMenuBg    = isDark ? TD.ink                        : '#fff';
  const mobileLinkColor = isDark ? 'rgba(255,255,255,0.8)'      : 'rgba(27,62,40,0.85)';
  const mobileLinkBorder= isDark ? 'rgba(255,255,255,0.05)'     : 'rgba(22,101,52,0.08)';
  const mobileSignInStyle = isDark
    ? { ...FF.label, fontWeight:600, color:'#fff', textAlign:'center', padding:12, borderRadius:8, border:'1px solid rgba(255,255,255,0.15)', textDecoration:'none' }
    : { ...FF.label, fontWeight:600, color:TD.forest, textAlign:'center', padding:12, borderRadius:8, border:`1px solid ${TD.forest}40`, textDecoration:'none' };
  const menuColors = isDark
    ? { link:navLinkColor, linkHover:TD.lime, panelBg:TD.ink, panelBorder:'rgba(182,238,105,0.15)', panelText:mobileLinkColor, panelMuted:'rgba(255,255,255,0.68)', accent:TD.lime, divider:mobileLinkBorder }
    : { link:navLinkColor, linkHover:overDark ? TD.lime : TD.forest, panelBg:'#fff', panelBorder:'rgba(22,101,52,0.14)', panelText:mobileLinkColor, panelMuted:'rgba(27,62,40,0.75)', accent:TD.forest, divider:mobileLinkBorder };
  return (
    <nav style={{ position:'fixed', top:0, left:0, right:0, zIndex:1500, transition:'all 0.35s ease', background:scrolled?navScrollBg:'transparent', backdropFilter:scrolled?'blur(22px)':'none', borderBottom:scrolled?`1px solid ${navScrollBorder}`:'1px solid transparent' }}>
      <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 24px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:20, height:68 }}>
        <Link to="/" style={{ display:'flex', alignItems:'center', gap:10, textDecoration:'none', flexShrink:0, whiteSpace:'nowrap' }}>
          <img src="/logo.svg" alt="TrashDrop" style={{ height:36, width:36 }} />
          <span style={{ ...FF.label, fontSize:18, fontWeight:700, color:brandColor, letterSpacing:'-0.3px' }}>Trash<span style={{ color:TD.lime }}>Drop</span></span>
        </Link>
        <PublicMenuDesktop className="td-nav-links" colors={menuColors} />
        <div className="td-nav-links" style={{ display:'flex', gap:10, alignItems:'center', flexShrink:0 }}>
          {isAuthenticated
            ? <Link to="/dashboard" className="td-nav-btn" style={{ ...(overDark ? BTN.primaryOnDark : BTN.primary), ...BTN.small }}>Dashboard</Link>
            : <><Link to="/login"  className="td-nav-btn" style={{ ...FF.label, fontSize:16, fontWeight:600, color:navLinkColor, textDecoration:'none', padding:'9px 14px' }}>Sign in</Link>
                <Link to="/#book-app" className="td-nav-btn" style={{ ...(overDark ? BTN.primaryOnDark : BTN.primary), ...BTN.small }}>Book a pickup</Link></>}
        </div>
        <button className="td-mobile-btn" onClick={() => setMobileOpen(o=>!o)} aria-label={mobileOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileOpen} style={{ background:'none', border:'none', cursor:'pointer', padding:8, color:isDark || !scrolled ? '#fff' : TD.forest }}>
          <i className={`fas ${mobileOpen?'fa-times':'fa-bars'}`} style={{ fontSize:18 }}></i>
        </button>
      </div>
      {mobileOpen && (
        <div style={{ background:mobileMenuBg, borderTop:`1px solid ${isDark?'rgba(182,238,105,0.12)':'rgba(22,101,52,0.1)'}`, padding:'8px 24px 24px', maxHeight:'calc(100vh - 68px)', overflowY:'auto' }}>
          <PublicMenuMobile colors={menuColors} onNavigate={() => setMobileOpen(false)} />
          <div style={{ marginTop:16, display:'flex', flexDirection:'column', gap:8 }}>
            <button onClick={toggleLp} style={{ ...FF.label, fontWeight:600, color:isDark?TD.lime:TD.forest, background:'transparent', border:`1px solid ${isDark?'rgba(182,238,105,0.25)':'rgba(22,101,52,0.2)'}`, textAlign:'center', padding:12, borderRadius:8, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
              <i className={`fas ${isDark?'fa-sun':'fa-moon'}`}></i> Switch to {isDark?'Light':'Dark'} Mode
            </button>
            {isAuthenticated
              ? <Link to="/dashboard" style={{ ...FF.label, fontWeight:700, background:TD.lime, color:TD.ink, textAlign:'center', padding:12, borderRadius:8, textDecoration:'none' }}>Go to Dashboard</Link>
              : <><Link to="/login"  style={mobileSignInStyle}>Sign In</Link>
                  <Link to="/#book-app" onClick={() => setMobileOpen(false)} style={{ ...(isDark ? BTN.primaryOnDark : BTN.primary), display:'flex' }}>Book a pickup</Link></>}
          </div>
        </div>
      )}
    </nav>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   BOOKING CARD (top of the hero)
   ═══════════════════════════════════════════════════════════════════════════════ */
const SERVICES = {
  bag: { label:'QR Bag pickup', note:'Free',      cta:'Request pickup' },
  bin: { label:'Digital Bin',   note:'Get quote', cta:'Get quote'      },
};

const BookingCard = () => {
  const [city, setCity] = useState(BOOKING_CITIES[0].name);
  const [location, setLocation] = useState('');
  const [service, setService] = useState('bag');
  const [locStatus, setLocStatus] = useState('');
  const [locating, setLocating] = useState(false);

  // Location is only requested when the visitor taps the button, never on load
  const useMyLocation = () => {
    if (!navigator.geolocation) { setLocStatus('Your browser cannot share your location. Type your area instead.'); return; }
    setLocating(true);
    setLocStatus('Finding you…');
    navigator.geolocation.getCurrentPosition(async ({ coords }) => {
      const near = nearestBookingCity(coords.latitude, coords.longitude);
      if (near) setCity(near);
      const address = await reverseGeocode(coords.latitude, coords.longitude);
      setLocation(address || `${coords.latitude.toFixed(5)}, ${coords.longitude.toFixed(5)}`);
      setLocStatus(near ? '' : 'We are not in your area yet. We serve Accra, Kumasi, Takoradi and Tamale.');
      setLocating(false);
    }, () => {
      setLocStatus('Location is off. Type your area instead.');
      setLocating(false);
    }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 });
  };

  const field = { width:'100%', boxSizing:'border-box', height:48, border:'1px solid #d7dbe0', borderRadius:10, padding:'0 52px 0 14px', ...FF.body, fontSize:16, color:TD.ink, background:'#fff' };
  return (
    // #book-app is the target of "Book a pickup" links across the site
    <div id="book-app" data-scroll-align="center" data-testid="booking-card" style={{ background:'#fff', borderRadius:'var(--td-radius-panel)', padding:24, maxWidth:480, boxShadow:'0 18px 50px rgba(0,0,0,0.35)', color:TD.ink, boxSizing:'border-box' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, ...FF.body, fontSize:16 }}>
        <span>Pickup in <strong data-testid="booking-city">{city}</strong></span>
        {/* A native picker under a "Change city" link: nothing on the page moves when it opens */}
        <label style={{ position:'relative', color:TD.forest, fontWeight:600, fontSize:14, cursor:'pointer', textDecoration:'underline' }}>
          Change city
          <select value={city} onChange={e => setCity(e.target.value)} aria-label="Change city"
            style={{ position:'absolute', inset:0, opacity:0, cursor:'pointer', width:'100%' }}>
            {BOOKING_CITIES.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
        </label>
      </div>

      <label htmlFor="booking-location" style={{ display:'block', ...TYPE.small, fontWeight:600, color:TD.sage, marginBottom:6 }}>Your location</label>
      <div style={{ position:'relative' }}>
        <input id="booking-location" value={location} onChange={e => setLocation(e.target.value)} placeholder="Area, street or landmark" autoComplete="street-address" style={field} />
        <button type="button" onClick={useMyLocation} disabled={locating} aria-label="Use my location" title="Use my location"
          style={{ position:'absolute', right:4, top:4, width:40, height:40, border:'none', borderRadius:8, background:`${TD.lime}30`, color:TD.forest, cursor:'pointer', fontSize:16 }}>
          <i className={`fas ${locating ? 'fa-spinner fa-spin' : 'fa-location-arrow'}`} aria-hidden="true"></i>
        </button>
      </div>
      <p role="status" style={{ ...FF.body, fontSize:13, color:TD.sage, minHeight:18, lineHeight:'18px', margin:'4px 0 10px', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>{locStatus}</p>

      <div role="radiogroup" aria-label="Service" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, background:'#f1f3f5', borderRadius:12, padding:4, marginBottom:14 }}>
        {Object.entries(SERVICES).map(([key, s]) => (
          <button key={key} type="button" role="radio" aria-checked={service === key} onClick={() => setService(key)}
            style={{ border:'none', borderRadius:9, padding:'9px 6px', cursor:'pointer', background: service === key ? '#fff' : 'transparent', boxShadow: service === key ? '0 1px 4px rgba(0,0,0,0.12)' : 'none', ...FF.body, color:TD.ink, lineHeight:1.25 }}>
            <span style={{ display:'block', fontSize:14, fontWeight:600 }}>{s.label}</span>
            <span style={{ display:'block', fontSize:13, color: key === 'bag' ? TD.forest : TD.sage }}>({s.note})</span>
          </button>
        ))}
      </div>

      <a href={HOUSEHOLD_APP_URL} data-testid="booking-submit"
        style={{ ...BTN.primary, display:'flex', width:'100%', minHeight:52 }}>
        <i className="fas fa-truck" aria-hidden="true"></i> {SERVICES[service].cta}
      </a>

      {/* A code to scan is for a second screen: phones book with the button above */}
      <div className="td-book-qr" style={{ display:'flex', alignItems:'center', gap:14, marginTop:16, paddingTop:14, borderTop:'1px solid #eceef1' }}>
        <img src="/images/book-in-app-qr.png" alt="QR code that opens the TrashDrop app" width="96" height="96" style={{ width:96, height:96, borderRadius:8, flexShrink:0 }} />
        <p style={{ ...TYPE.small, color:TD.sage, margin:0 }}>
          <strong style={{ color:TD.ink }}>Scan to book in the TrashDrop app.</strong> Point your phone camera at the code.
        </p>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   HERO
   ═══════════════════════════════════════════════════════════════════════════════ */
// Three facts that used to float around the hero, now one quiet row
const HERO_FACTS = [
  { icon:'fa-qrcode',     title:'Free with QR Bags', text:'Collection is included with every bag' },
  { icon:'fa-bolt',       title:'Digital Bin',       text:'An instant price, pickup on demand' },
  { icon:'fa-location-arrow', title:'Track it live', text:'Follow your collector to your door' },
];

const HeroSection = () => (
  <section data-surface="dark" style={{ background:TD.ink, padding:'calc(var(--td-header-h) + 56px) 24px var(--td-section-y)' }}>
    <div className="td-hero-grid" style={{ maxWidth:1200, margin:'0 auto', display:'grid', gridTemplateColumns:'minmax(0, 1fr) minmax(0, 0.9fr)', gap:64, alignItems:'center' }}>
      <div style={{ minWidth:0 }}>
        <h1 style={{ ...TYPE.h1, color:'#f0f5f0', margin:'0 0 16px', maxWidth:620, textWrap:'balance' }}>
          Waste pickup in Ghana, booked in minutes
        </h1>
        <p style={{ ...TYPE.lead, color:TD.sageOnDark, margin:'0 0 32px', maxWidth:540 }}>
          A verified collector comes to your door. Pay with MoMo or cash — or nothing at all with TrashDrop bags.
        </p>
        <div id="book" style={{ scrollMarginTop:88 }}><BookingCard /></div>
        <a href={BUSINESS.whatsappUrl} target="_blank" rel="noopener noreferrer"
          style={{ display:'inline-flex', alignItems:'center', gap:8, color:'#f0f5f0', ...TYPE.small, fontWeight:600, textDecoration:'none', marginTop:16 }}>
          <i className="fab fa-whatsapp" style={{ color:'#25D366', fontSize:18 }} aria-hidden="true"></i> Or book on WhatsApp: {BUSINESS.phoneDisplay}
        </a>
      </div>

      {/* The service, as it happens: a collector at a household's gate */}
      <figure className="td-hero-photo" style={{ margin:0, minWidth:0 }}>
        <img src="/images/how-it-works/pickup-at-gate.jpg" width="720" height="1073" alt="A TrashDrop collector in a hi-vis vest takes a household's full bag at their gate"
          style={{ display:'block', width:'100%', maxWidth:460, aspectRatio:'4 / 5', objectFit:'cover', borderRadius:'var(--td-radius-panel)', marginLeft:'auto' }} />
      </figure>
    </div>

    <ul className="td-hero-facts" style={{ listStyle:'none', maxWidth:1200, margin:'56px auto 0', padding:'28px 0 0', borderTop:'1px solid rgba(255,255,255,0.12)', display:'grid', gridTemplateColumns:'repeat(3, minmax(0, 1fr))', gap:24 }}>
      {HERO_FACTS.map(f => (
        <li key={f.title} style={{ display:'flex', gap:14, alignItems:'flex-start', minWidth:0 }}>
          <i className={`fas ${f.icon}`} aria-hidden="true" style={{ color:TD.lime, fontSize:18, marginTop:3, width:20, textAlign:'center' }}></i>
          <span>
            <span style={{ display:'block', ...TYPE.body, fontWeight:600, color:'#f0f5f0' }}>{f.title}</span>
            <span style={{ display:'block', ...TYPE.small, color:TD.sageOnDark }}>{f.text}</span>
          </span>
        </li>
      ))}
    </ul>
  </section>
);

/* ═══════════════════════════════════════════════════════════════════════════════
   TRUST ROW (values in src/config/homepage.js)
   ═══════════════════════════════════════════════════════════════════════════════ */
const TrustRow = () => (
  <section aria-label="Why households trust TrashDrop" style={{ background:TD.ink, borderTop:'1px solid rgba(255,255,255,0.08)' }}>
    <ul className="td-trust" style={{ listStyle:'none', margin:'0 auto', maxWidth:1100, padding:'22px 24px', display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16 }}>
      {TRUST_STATS.map(t => (
        <li key={t.label} style={{ textAlign:'center' }}>
          <span style={{ display:'block', ...TYPE.stat, color:TD.lime }}>{t.value}</span>
          <span style={{ display:'block', ...TYPE.small, color:TD.sageOnDark }}>{t.label}</span>
        </li>
      ))}
    </ul>
  </section>
);

/* ═══════════════════════════════════════════════════════════════════════════════
   SERVICES
   ═══════════════════════════════════════════════════════════════════════════════ */
const APP_SCREENS = [
  { Screen: QrScanScreen, title: 'Scan your QR bags', body: 'Scan the code on a new pack once and every bag in it is on your account.' },
  { Screen: BookScreen, title: 'Book a pickup', body: 'Choose the waste and the time. You see the price before you confirm.' },
  { Screen: TrackScreen, title: 'Follow your collector', body: 'See who is coming and watch them on the map until your waste is collected.' },
];
const ServicesSection = () => {
  const { ref, visible } = useReveal(0.08);
  const { lp } = useLpTheme();
  return (
    <section id="services" style={{ background:lp.sectionAlt, paddingTop:'var(--td-section-y)', paddingBottom:'var(--td-section-y)' }}>
      <div ref={ref} style={{ maxWidth:1280, margin:'0 auto', padding:'0 24px' }}>
        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:6 }}>
          <span style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', color:lp.sectionLabel, textTransform:'uppercase' }}>Our Services</span>
        </div>
        <h2 style={{ ...TYPE.h2, color:lp.ink2Heading, margin:'0 0 14px', opacity:visible?1:0, transform:visible?'translateY(0)':'translateY(28px)', transition:'all 0.9s cubic-bezier(.22,1,.36,1)' }}>
          Two ways to <em style={{ color:TD.forest }}>drop</em> your waste.<br />Zero excuses.
        </h2>
        <p style={{ ...FF.body, fontSize:16, fontWeight:400, color:lp.sageText, maxWidth:540, marginBottom:48, lineHeight:1.75 }}>
          TrashDrop offers two ways to book waste collection, built for how you actually live: QR Bags for regular rubbish collection
          with free pickup, or the Digital Bin for an on-demand trash pickup whenever you need one.
        </p>
        <div className="td-services-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
          {/* QR Bag */}
          <div style={{ background:TD.ink2, borderRadius:20, padding:'40px 44px', position:'relative', overflow:'hidden', opacity:visible?1:0, transform:visible?'translateY(0)':'translateY(40px)', transition:'all 0.9s cubic-bezier(.22,1,.36,1) 0.1s' }}>
            <div style={{ position:'absolute', top:-40, right:-40, width:220, height:220, borderRadius:'50%', background:`${TD.lime}0C`, pointerEvents:'none' }} />
            <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:`${TD.lime}18`, border:`1px solid ${TD.lime}30`, borderRadius:99, padding:'5px 12px', marginBottom:20 }}>
              <span style={{ width:5, height:5, borderRadius:'50%', background:TD.lime, display:'inline-block' }} />
              <span style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', color:TD.lime, textTransform:'uppercase' }}>Service One</span>
            </div>
            <h3 style={{ ...TYPE.h3, fontSize:'clamp(24px,2.6vw,30px)', color:'#fff', margin:'0 0 8px' }}>The QR<br /><em style={{ color:TD.lime }}>Bag System</em></h3>
            <p style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'3px', color:TD.lime, textTransform:'uppercase', marginBottom:20 }}>Buy · Scan · Collect</p>
            <div style={{ width:36, height:2, background:TD.lime, borderRadius:99, marginBottom:28 }} />
            <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
              {[
                ['1','Purchase Official Bags','Buy TrashDrop bags from any authorised vendor near you.'],
                ['2','Scan the QR Code','Each pack of bags has its own code. Scan it once to add all the bags to your account.'],
                ['3','Request Free Collection','When your bag is full, tap to request pickup — collection is completely free.']
              ].map(([n,t,d]) => (
                <div key={n} style={{ display:'flex', gap:14 }}>
                  <div style={{ width:28, height:28, borderRadius:'50%', background:`${TD.lime}20`, border:`1px solid ${TD.lime}35`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1 }}>
                    <span style={{ ...FF.label, fontSize:13, fontWeight:700, color:TD.lime }}>{n}</span>
                  </div>
                  <div>
                    <p style={{ ...FF.label, fontSize:13, fontWeight:700, color:TD.lime, letterSpacing:'0.5px', margin:'0 0 3px' }}>{t}</p>
                    <p style={{ ...FF.body, fontSize:13, fontWeight:400, color:'rgba(255,255,255,0.9)', margin:0, lineHeight:1.65 }}>{d}</p>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, marginTop:28, background:TD.lime, color:TD.ink, padding:'10px 20px', borderRadius:8, ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em' }}>✦ Collection is FREE</div>
          </div>
          {/* Digital Bin */}
          <div style={{ background:TD.forest, borderRadius:20, padding:'40px 44px', position:'relative', overflow:'hidden', opacity:visible?1:0, transform:visible?'translateY(0)':'translateY(40px)', transition:'all 0.9s cubic-bezier(.22,1,.36,1) 0.22s' }}>
            <div style={{ position:'absolute', bottom:-40, left:-40, width:200, height:200, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }} />
            <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.3)', borderRadius:99, padding:'5px 12px', marginBottom:20 }}>
              <span style={{ width:5, height:5, borderRadius:'50%', background:'#fff', display:'inline-block' }} />
              <span style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', color:'#fff', textTransform:'uppercase' }}>Service Two</span>
            </div>
            <h3 style={{ ...TYPE.h3, fontSize:'clamp(24px,2.6vw,30px)', color:'#fff', margin:'0 0 8px' }}>The Digital<br /><em style={{ color:'#ffffff' }}>Bin Service</em></h3>
            <p style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', color:'rgba(255,255,255,0.9)', textTransform:'uppercase', marginBottom:20 }}>Request · Quote · Track</p>
            <div style={{ width:36, height:2, background:'rgba(255,255,255,0.4)', borderRadius:99, marginBottom:28 }} />
            <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
              {[['1','Request On-Demand Pickup','Open the app and submit a Request Bin Pickup (No Bag) anytime, anywhere.'],
                ['2','Instant Price Quote','Get a clear price straight away — no surprises.'],
                ['3','Real-Time Collector Tracking','Your request is matched to a nearby collector. Track arrival live on the map.']].map(([n,t,d]) => (
                <div key={n} style={{ display:'flex', gap:14 }}>
                  <div style={{ width:28, height:28, borderRadius:'50%', background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.3)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1 }}>
                    <span style={{ ...FF.label, fontSize:13, fontWeight:700, color:'#fff' }}>{n}</span>
                  </div>
                  <div>
                    <p style={{ ...FF.label, fontSize:13, fontWeight:700, color:'#ffffff', letterSpacing:'0.5px', margin:'0 0 3px' }}>{t}</p>
                    <p style={{ ...FF.body, fontSize:13, fontWeight:400, color:'rgba(255,255,255,0.9)', margin:0, lineHeight:1.65 }}>{d}</p>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, marginTop:28, background:'rgba(255,255,255,0.18)', border:'1px solid rgba(255,255,255,0.3)', color:'#fff', padding:'10px 20px', borderRadius:8, ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em' }}>📍 Live Tracking</div>
          </div>
        </div>
        {/* The same services in the app */}
        <ul className="td-app-screens" style={{ listStyle:'none', margin:'48px 0 0', padding:0, display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(min(260px,100%),1fr))', gap:'8px 24px' }}>
          {APP_SCREENS.map(({ Screen, title, body }) => (
            <li key={title} style={{ textAlign:'center' }}>
              <Screen />
              <h3 style={{ ...TYPE.card, color:lp.ink2Heading, margin:'4px 0 6px' }}>{title}</h3>
              <p style={{ ...TYPE.small, color:lp.sageText, margin:'0 auto', maxWidth:300 }}>{body}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   TRUST (recommendation 11 of the design review, wording approved by the owner)
   ═══════════════════════════════════════════════════════════════════════════════ */
const TRUST_POINTS = [
  { icon:'fa-id-card', title:'Verified collectors', body:'Every collector is ID-checked and has their vehicle checked before they join. You see who is coming before they arrive.' },
  { icon:'fa-map-marker-alt', title:'Follow your pickup live', body:'Watch your collector on the map from the moment they accept until your waste is collected.' },
  { icon:'fa-tag', title:'The price comes first', body:'Digital Bin pickups show the price before you confirm, and QR Bag pickups are free. Pay with MoMo or cash at collection.' },
  { icon:'fa-user-secret', title:'Reports stay anonymous', body:'The public dumping map shows where a dump is and what\'s in it. It never shows who reported it.' },
  { icon:'fa-recycle', title:'Licensed disposal', body:'Collected waste goes only to sites with a valid Ghana EPA permit, never to a roadside or drain.' },
];

const TrustSection = () => (
  <section id="trust" aria-labelledby="trust-title" style={{ background:'#fff', padding:'var(--td-section-y) 24px' }}>
    <div style={{ maxWidth:1200, margin:'0 auto' }}>
      <p style={{ ...TYPE.eyebrow, color:TD.forest, margin:'0 0 12px' }}>Why people trust TrashDrop</p>
      <h2 id="trust-title" style={{ ...TYPE.h2, color:TD.ink, margin:'0 0 14px' }}>Safe for your home and your street</h2>
      <p style={{ ...TYPE.lead, color:TD.sage, maxWidth:620, margin:0 }}>
        You know who is coming, what it costs and where your waste goes. Reporting a dump never puts your name on the map.
      </p>
      <ul style={{ listStyle:'none', padding:0, margin:'40px 0 0', display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(min(200px,100%),1fr))', gap:16 }}>
        {TRUST_POINTS.map(t => (
          <li key={t.title} style={{ background:TD.cream, border:'1px solid var(--td-border)', borderRadius:12, padding:22, display:'flex', flexDirection:'column', gap:8 }}>
            <span aria-hidden="true" style={{ width:44, height:44, borderRadius:10, background:'#e6f2e9', display:'grid', placeItems:'center', color:TD.forest, fontSize:18 }}>
              <i className={`fas ${t.icon}`}></i>
            </span>
            <h3 style={{ ...TYPE.card, fontSize:18, color:TD.ink, margin:'6px 0 0' }}>{t.title}</h3>
            <p style={{ ...TYPE.small, fontSize:15, color:TD.sage, margin:0 }}>{t.body}</p>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

const RISK_PILL = { Critical:'#dc2626', High:'#ea580c', Medium:'#eab308', Low:'#22c55e' };

/* ─── Latest reports overlay (absolute, bottom-right of map) ──────────────────
   The newest real reports from TrashDrop users, from the same public data as the
   map (no reporter details). Refreshes with the map, every minute. */
const FEED_FILTERS = [['all', 'All'], ['active', 'Waiting'], ['cleaned', 'Cleaned up']];
const severityLabel = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Medium');

const LatestReportsOverlay = ({ data }) => {
  const { reports, status } = data;
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [freshIds, setFreshIds] = useState([]);
  const seen = useRef(null);

  // Highlight reports that arrive on a refresh (not the first load)
  useEffect(() => {
    if (status !== 'ready') return undefined;
    const ids = reports.map(r => r.id);
    if (seen.current) {
      const added = ids.filter(id => !seen.current.has(id));
      if (added.length) {
        setFreshIds(added);
        const t = setTimeout(() => setFreshIds([]), 4000);
        seen.current = new Set(ids);
        return () => clearTimeout(t);
      }
    }
    seen.current = new Set(ids);
    return undefined;
  }, [reports, status]);

  const items = latestReports(reports, filter, 15);
  const recentCount = latestReports(reports, 'all', 15).length;

  const panelBg    = 'rgba(8,12,8,0.93)';
  const borderCol  = 'rgba(182,238,105,0.18)';
  const headText   = '#f0f5f0';
  const subText    = 'rgba(255,255,255,0.5)';
  const cardBg     = 'rgba(255,255,255,0.04)';
  const cardBorder = 'rgba(255,255,255,0.08)';
  const metaTxt    = 'rgba(255,255,255,0.45)';
  const pillBg     = 'rgba(255,255,255,0.07)';

  return (
    <div data-testid="latest-reports-overlay" className="td-map-hide-with-card" style={{ position:'absolute', bottom:16, right:16, zIndex:1150, display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8, maxHeight:'calc(100% - 80px)', maxWidth:'calc(100% - 32px)' }}>
      {open && (
        <div id="latest-reports" role="region" aria-label="Latest reports" style={{ width:320, maxWidth:'100%', minHeight:0, flex:'0 1 auto', background:panelBg, border:`1px solid ${borderCol}`, borderRadius:18, overflow:'hidden', backdropFilter:'blur(20px)', boxShadow:'0 24px 60px rgba(0,0,0,0.7)', display:'flex', flexDirection:'column' }}>
          <div style={{ padding:'11px 14px', borderBottom:`1px solid ${borderCol}`, display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ width:7, height:7, borderRadius:'50%', background:TD.lime, flexShrink:0, boxShadow:`0 0 0 3px ${TD.lime}28`, animation:'td-pulse-dot 1.8s infinite' }} />
            <span style={{ ...FF.label, fontSize:13, fontWeight:700, color:headText, letterSpacing:'0.08em', textTransform:'uppercase', flex:1 }}>Latest reports</span>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close latest reports" style={{ width:22, height:22, borderRadius:6, border:`1px solid ${borderCol}`, background:'transparent', color:subText, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13 }}>✕</button>
          </div>

          <div style={{ padding:'7px 12px', borderBottom:`1px solid ${borderCol}`, display:'flex', gap:5, flexWrap:'wrap' }}>
            {FEED_FILTERS.map(([key, label]) => {
              const active = filter === key;
              return (
                <button key={key} type="button" onClick={() => setFilter(key)} aria-pressed={active} style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.8px', textTransform:'uppercase', padding:'3px 10px', borderRadius:16, cursor:'pointer', background: active ? TD.forest : pillBg, color: active ? '#fff' : 'rgba(255,255,255,0.55)', border: active ? 'none' : `1px solid ${borderCol}` }}>
                  {label}
                </button>
              );
            })}
          </div>

          <div style={{ maxHeight:340, minHeight:0, flex:'1 1 auto', overflowY:'auto', padding:'10px 12px', display:'flex', flexDirection:'column', gap:8 }}>
            {status === 'loading' && <div style={{ padding:'24px 0', textAlign:'center', color:subText, ...FF.body, fontSize:13 }}>Loading reports…</div>}
            {status === 'error' && <div style={{ padding:'24px 0', textAlign:'center', color:subText, ...FF.body, fontSize:13 }}>Reports could not load right now. Try again in a minute.</div>}
            {status === 'ready' && items.length === 0 && (
              <div style={{ padding:'24px 0', textAlign:'center', color:subText, ...FF.body, fontSize:13 }}>{filter === 'all' ? 'No reports yet.' : 'No reports in this view yet.'}</div>
            )}
            {items.map(r => {
              const sev = severityLabel(r.severity);
              const sevColor = RISK_PILL[sev] || '#eab308';
              const fresh = freshIds.includes(r.id);
              const cleanedText = r.isCleanedUp ? (cleanupDurationText(r.createdAt, r.completedAt) || 'Cleaned up') : null;
              return (
                <div key={r.id} style={{ display:'flex', gap:10, background: fresh ? 'rgba(182,238,105,0.07)' : cardBg, border:`1px solid ${fresh ? TD.lime + '40' : cardBorder}`, borderRadius:12, padding:'9px 10px', transition:'all 0.4s ease' }}>
                  {r.photos[0]
                    ? <img src={r.photos[0]} alt={`Reported dumping at ${describeArea(r)}`} width="48" height="48" loading="lazy" decoding="async" style={{ width:48, height:48, borderRadius:8, objectFit:'cover', flexShrink:0, background:'#1a221a' }} />
                    : <div aria-hidden="true" style={{ width:48, height:48, borderRadius:8, background:'rgba(255,255,255,0.06)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, color:metaTxt }}><i className="fas fa-trash-alt" style={{ fontSize:14 }}></i></div>}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <span style={{ ...FF.label, fontSize:13, fontWeight:700, color:headText, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>{describeArea(r)}</span>
                      {fresh && <span style={{ ...FF.label, fontSize:13, fontWeight:700, color:TD.lime, background:`${TD.lime}20`, padding:'1px 5px', borderRadius:3 }}>NEW</span>}
                      <span style={{ ...FF.label, fontSize:13, fontWeight:700, color:sevColor, background:sevColor + '18', padding:'2px 7px', borderRadius:5, textTransform:'uppercase', flexShrink:0 }}>{sev}</span>
                    </div>
                    <div style={{ ...FF.body, fontSize:13, color:metaTxt, marginTop:2 }}>
                      Reported {timeAgo(r.createdAt)}{r.wasteType ? ` · ${r.wasteType.replace(/_/g, ' ')} waste` : ''}
                    </div>
                    <div style={{ ...FF.body, fontSize:13, fontWeight:600, marginTop:4, color: r.isCleanedUp ? '#4ade80' : TD.orange }}>
                      {r.isCleanedUp ? `✓ ${cleanedText}` : 'Waiting for cleanup'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ padding:'8px 14px', borderTop:`1px solid ${borderCol}`, display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
            <span style={{ ...FF.body, fontSize:13, color:subText }}>Real reports from TrashDrop users · updates every minute</span>
            <a href="/illegal-dumping/" style={{ ...FF.label, fontSize:13, fontWeight:700, color:TD.lime, textDecoration:'none', whiteSpace:'nowrap' }}>Report a dump →</a>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-controls="latest-reports"
        style={{ display:'flex', alignItems:'center', gap:8, background:'rgba(8,12,8,0.92)', border:`1px solid ${borderCol}`, borderRadius:12, padding:'9px 14px', cursor:'pointer', backdropFilter:'blur(16px)', boxShadow:'0 8px 32px rgba(0,0,0,0.5)', transition:'all 0.2s' }}
      >
        <span style={{ width:7, height:7, borderRadius:'50%', background:TD.lime, flexShrink:0, boxShadow:`0 0 0 2px ${TD.lime}28`, animation:'td-pulse-dot 1.8s infinite' }} />
        <span style={{ ...FF.label, fontSize:13, fontWeight:700, color:headText, letterSpacing:'0.08em', textTransform:'uppercase' }}>
          {open ? 'Hide reports' : 'Latest reports'}
        </span>
        {!open && recentCount > 0 && (
          <span style={{ ...FF.label, fontSize:13, fontWeight:700, color:TD.ink, background:TD.lime, padding:'1px 7px', borderRadius:10 }}>{recentCount}</span>
        )}
        <i className={`fas fa-chevron-${open ? 'down' : 'up'}`} style={{ fontSize:13, color:subText }} aria-hidden="true" />
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   LIVE MAP
   ═══════════════════════════════════════════════════════════════════════════════ */
// applyThresholds (homepage): hide Cleanup Rate / Avg Response until they pass PUBLIC_STAT_THRESHOLDS
// home: the homepage's shorter version — "Trash Map" heading, map only (no intro, button or phone mockup)
const MapPreviewSection = ({ isAuthenticated, dumpingData, applyThresholds = false, home = false }) => {
  const navigate = useNavigate();
  const { ref, visible } = useReveal(0.05);
  const { lp, lpMode } = useLpTheme();
  const stats = computePublicStats(dumpingData.reports);
  const statsReady = dumpingData.status === 'ready';
  const show = !applyThresholds ? { cleanupRate: true, avgResponse: true }
    : statsReady ? publicStatVisibility(stats, PUBLIC_STAT_THRESHOLDS) : { cleanupRate: false, avgResponse: false };
  const tiles = [
    {label:'Total Reports',value:String(stats.total),icon:'fa-file-alt',color:'#4d9de0',bg:'rgba(77,157,224,0.12)', title:'Public reports on the map (excludes cancelled reports)'},
    {label:'Hotspot Zones',value:String(stats.hotspots),icon:'fa-map-pin',color:'#dc2626',bg:'rgba(220,38,38,0.12)', title:`Areas where ${HOTSPOT_MIN_REPORTS}+ active reports are each within 500 m of another`},
    show.cleanupRate && {label:'Cleanup Rate',value:stats.cleanupRate === null ? '—' : `${Math.round(stats.cleanupRate)}%`,icon:'fa-broom',color:TD.lime,bg:`${TD.lime}14`, title:`${stats.cleanedUp} of ${stats.total} reports cleaned up`},
    show.avgResponse && {label:'Avg Response',value:formatDuration(stats.avgResponseHours),icon:'fa-clock',color:TD.orange,bg:`${TD.orange}14`, title:'Average time from report to cleanup, for cleaned-up reports'},
  ].filter(Boolean);
  // A report's "View more details": visitors sign up first; signed-in users open the analytics map
  const detailsHref = isAuthenticated ? '/illegal-dumping/map' : '/signup';
  return (
    <section id="map" style={{ background:lp.mapSectionBg, padding:'var(--td-section-y) 0' }}>
      <div ref={ref} style={{ maxWidth:1280, margin:'0 auto', padding:'0 24px' }}>
        <div style={{ textAlign:'center', marginBottom:52 }}>
          <span style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', color:lpMode === 'dark' ? TD.lime : TD.forest, textTransform:'uppercase', display:'block', marginBottom:14, opacity:visible?1:0, transition:'opacity 0.6s ease' }}>Illegal Dumping Live Map</span>
          <h2 style={{ ...TYPE.h2, color:lp.sectionHeading, margin:home ? 0 : '0 0 14px', opacity:visible?1:0, transform:visible?'translateY(0)':'translateY(28px)', transition:'all 0.9s cubic-bezier(.22,1,.36,1) 0.1s' }}>
            {home
              ? <>Trash Map — <em style={{ color:lpMode === 'dark' ? TD.lime : TD.forest }}>illegal dumping</em> hotspot</>
              : <>Track. Report. <em style={{ color:lpMode === 'dark' ? TD.lime : TD.forest }}>Eliminate</em> Illegal Dumping.</>}
          </h2>
          {!home && <>
          <p style={{ ...FF.body, fontSize:16, fontWeight:400, color:lp.bodyText, maxWidth:620, margin:'0 auto 24px', lineHeight:1.75 }}>
            Beyond waste collection, TrashDrop helps communities, cities and environmental agencies across Ghana detect, report and clean up
            illegal dumping. Explore reported dumping sites across Ghana in real time.
          </p>
          <button
            onClick={() => { if (isAuthenticated) navigate('/illegal-dumping/reports'); else window.location.href = HOUSEHOLD_APP_URL; }}
            style={{ display:'inline-flex', alignItems:'center', gap:10, background:'transparent', color:lp.sectionHeading, ...FF.label, fontSize:14, fontWeight:700, padding:'12px 24px', borderRadius:10, cursor:'pointer', border:`1.5px solid ${TD.lime}55` }}>
            <i className="fas fa-exclamation-triangle" style={{ color:TD.orange }}></i> Report Illegal Dump
          </button>
          </>}
        </div>
        
        {/* Two Column Layout: Mobile on Left, Map on Right (map only on the homepage) */}
        <div style={{ display:'flex', gap:32, alignItems:'stretch', flexWrap:'wrap' }}>
          {/* Left: Mobile Report Mockup */}
          {!home && <div style={{ 
            display:'flex', 
            flexDirection:'column',
            gap:20,
            flex:1,
            minWidth:300,
            maxWidth:320
          }}>
            <ReportFlowScreen />
            
            {/* Feature List below phone */}
            <div style={{ 
              background:lp.featureBoxBg, 
              border:`1px solid ${lp.featureBoxBorder}`, 
              borderRadius:16, 
              padding:20
            }}>
              <p style={{ ...FF.label, fontSize:13, color:lp.featureTitle, fontWeight:700, marginBottom:14, letterSpacing:'0.5px' }}>
                <i className="fas fa-mobile-alt" style={{ marginRight:8 }}></i> Mobile Reporting
              </p>
              {[
                { icon:'fa-camera', text:'Snap photos of dumping sites' },
                { icon:'fa-map-marker-alt', text:'Auto GPS location tagging' },
                { icon:'fa-paper-plane', text:'Instant report submission' },
                { icon:'fa-bell', text:'Real-time status updates' }
              ].map((f, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:i<3?10:0 }}>
                  <div style={{ width:26, height:26, borderRadius:6, background:lp.featureIconBg, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <i className={`fas ${f.icon}`} style={{ color:lp.featureIcon, fontSize:13 }}></i>
                  </div>
                  <span style={{ ...FF.body, fontSize:13, color:lp.featureText }}>{f.text}</span>
                </div>
              ))}
            </div>
            
          </div>}

          {/* Right: Map (real reports, read-only) */}
          <div style={{ flex:2, minWidth:300 }}>
            <PublicDumpingMap lp={lp} isDark={lpMode === 'dark'} data={dumpingData} detailsHref={detailsHref} expandable>
              {/* Social feed overlay — bottom-right corner of the map */}
              <LatestReportsOverlay data={dumpingData} />
            </PublicDumpingMap>
          </div>
        </div>

        <div className="td-map-stats" style={{ display:'grid', gridTemplateColumns:`repeat(${tiles.length},1fr)`, gap:16, marginTop:24 }}>
          {tiles.map(s => (
            <div key={s.label} data-testid="map-stat" title={s.title} style={{ background:lp.mapStatBg, border:`1px solid ${lp.mapStatBorder}`, borderRadius:14, padding:'16px 20px', display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:44, height:44, borderRadius:12, background:s.bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <i className={`fas ${s.icon}`} style={{ color:s.color, fontSize:16 }}></i>
              </div>
              <div>
                <p style={{ ...TYPE.stat, color:lp.mapStatText, margin:0 }}>{statsReady ? s.value : '—'}</p>
                <p style={{ ...FF.label, fontSize:13, color:lp.mapStatLabel, fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', margin:'3px 0 0' }}>{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   IMPACT STATS
   ═══════════════════════════════════════════════════════════════════════════════ */
/* ─── Lightbox ───────────────────────────────────────────────────────────────── */
/** Alt text for the illegal-dumping photos (they show reported dump sites) */
const dumpPhotoAlt = (p) => `Illegal dumping reported on TrashDrop: ${p.label}`;

const DUMP_PHOTOS = [
  { src:'/images/chocked_bridge.jpeg',               label:'Bridge blockage · Accra' },
  { src:'/images/chocked_drainage_1.jpeg',           label:'Drainage blockage' },
  { src:'/images/chocked_pavement_1.jpeg',           label:'Pavement dump' },
  { src:'/images/chocked_drainage_2.jpeg',           label:'Drainage overflow' },
  { src:'/images/ksi_central_market.jpeg',           label:'Kumasi Central Market' },
  { src:'/images/chocked_pavement_2.jpeg',           label:'Pavement · Accra' },
  { src:'/images/chocked_pavement_3.jpeg',           label:'Roadside dump' },
  { src:'/images/chocked_pavement_4.jpeg',           label:'Blocked walkway' },
  { src:'/images/report_communal_bin_overflow.jpeg', label:'Bin overflow' },
];

const Lightbox = ({ photos, index, onClose, onPrev, onNext, onGoTo }) => {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext]);

  const photo = photos[index];
  return (
    <div
      onClick={onClose}
      style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.92)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      {/* Close */}
      <button onClick={onClose} style={{ position:'absolute', top:20, right:24, background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.15)', color:'#fff', width:44, height:44, borderRadius:'50%', cursor:'pointer', fontSize:18, display:'flex', alignItems:'center', justifyContent:'center', zIndex:10000 }}>
        <i className="fas fa-times" />
      </button>
      {/* Counter */}
      <div style={{ position:'absolute', top:24, left:24, ...FF.label, fontSize:13, color:'rgba(255,255,255,0.68)', fontWeight:600, letterSpacing:'0.08em' }}>
        {index + 1} / {photos.length}
      </div>
      {/* Prev */}
      <button onClick={e=>{e.stopPropagation();onPrev();}} style={{ position:'absolute', left:16, background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.14)', color:'#fff', width:48, height:48, borderRadius:'50%', cursor:'pointer', fontSize:18, display:'flex', alignItems:'center', justifyContent:'center', zIndex:10000, transition:'background 0.2s' }}
        onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.16)'}
        onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.08)'}>
        <i className="fas fa-chevron-left" />
      </button>
      {/* Image */}
      <div onClick={e=>e.stopPropagation()} style={{ maxWidth:'90vw', maxHeight:'85vh', display:'flex', flexDirection:'column', alignItems:'center', gap:16 }}>
        <img src={photo.src} alt={dumpPhotoAlt(photo)} style={{ maxWidth:'100%', maxHeight:'78vh', borderRadius:12, objectFit:'contain', boxShadow:'0 32px 80px rgba(0,0,0,0.7)' }} />
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ width:6, height:6, borderRadius:'50%', background:TD.lime, display:'inline-block', flexShrink:0 }} />
          <span style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', color:'rgba(255,255,255,0.75)', textTransform:'uppercase' }}>{photo.label}</span>
        </div>
        {/* Thumbnail strip */}
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', justifyContent:'center', maxWidth:600 }}>
          {photos.map((p,i) => (
            <button key={i} onClick={e=>{e.stopPropagation();onGoTo(i);}} style={{ padding:0, border:`2px solid ${i===index?TD.lime:'transparent'}`, borderRadius:6, overflow:'hidden', cursor:'pointer', background:'none', transition:'border-color 0.2s', width:44, height:44, flexShrink:0 }}>
              <img src={p.src} alt={dumpPhotoAlt(p)} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
            </button>
          ))}
        </div>
      </div>
      {/* Next */}
      <button onClick={e=>{e.stopPropagation();onNext();}} style={{ position:'absolute', right:16, background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.14)', color:'#fff', width:48, height:48, borderRadius:'50%', cursor:'pointer', fontSize:18, display:'flex', alignItems:'center', justifyContent:'center', zIndex:10000, transition:'background 0.2s' }}
        onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.16)'}
        onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.08)'}>
        <i className="fas fa-chevron-right" />
      </button>
    </div>
  );
};

const ImpactStatCard = ({ label, value, ready, icon, color, accent }) => {
  const { ref, visible } = useReveal(0.3);
  const { lp } = useLpTheme();
  const count = useCountUp(value, 2200, visible);
  return (
    <div ref={ref} style={{ textAlign:'center', padding:'36px 20px' }}>
      <div style={{ width:64, height:64, borderRadius:18, background:accent, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px', transition:'transform 0.3s ease', cursor:'default' }}
        onMouseEnter={e=>e.currentTarget.style.transform='scale(1.1) rotate(-5deg)'}
        onMouseLeave={e=>e.currentTarget.style.transform='scale(1) rotate(0deg)'}>
        <i className={`fas ${icon}`} style={{ fontSize:24, color }}></i>
      </div>
      <div style={{ display:'flex', alignItems:'baseline', justifyContent:'center', gap:2 }}>
        <span style={{ ...FF.data, fontSize:'clamp(44px,5vw,64px)', color:lp.impactCount, letterSpacing:'1px' }}>{ready ? count.toLocaleString() : '—'}</span>
      </div>
      <p style={{ ...FF.body, fontSize:13, color:lp.impactSub, fontWeight:400, margin:'6px 0 0' }}>{label}</p>
    </div>
  );
};

// applyThresholds (homepage): hide the number row until the cleanup rate passes PUBLIC_STAT_THRESHOLDS
const ImpactSection = ({ dumpingData, applyThresholds = false }) => {
  const { ref, visible } = useReveal(0.08);
  const { lp } = useLpTheme();
  const [lbIndex, setLbIndex] = useState(null);
  const lbOpen  = lbIndex !== null;
  const lbClose = () => setLbIndex(null);
  const lbPrev  = () => setLbIndex(i => (i - 1 + DUMP_PHOTOS.length) % DUMP_PHOTOS.length);
  const lbNext  = () => setLbIndex(i => (i + 1) % DUMP_PHOTOS.length);
  // Same reports as the live map above, so the two sections always agree
  const stats = computePublicStats(dumpingData.reports);
  const ready = dumpingData.status === 'ready';
  const showNumbers = !applyThresholds || (ready && publicStatVisibility(stats, PUBLIC_STAT_THRESHOLDS).cleanupRate);
  const STATS = [
    { label:'Dumps Reported',     value:stats.total,     icon:'fa-flag',          color:'#f87171', accent:'rgba(248,113,113,0.15)' },
    { label:'Cleaned Up',         value:stats.cleanedUp, icon:'fa-broom',         color:TD.lime,   accent:`${TD.lime}18`            },
    { label:'Waiting for Cleanup', value:stats.active,   icon:'fa-hourglass-half', color:TD.orange,  accent:`${TD.orange}18`            },
    { label:'Areas Reported',     value:stats.areas,     icon:'fa-map-marker-alt', color:'#60a5fa', accent:'rgba(96,165,250,0.15)'   },
  ];
  return (
    <section id="impact" style={{ background:lp.sectionForest, position:'relative', overflow:'hidden' }}>
      <div style={{ position:'absolute', inset:0, opacity:0.04, backgroundImage:`radial-gradient(circle at 1px 1px,rgba(182,238,105,0.5) 1px,transparent 0)`, backgroundSize:'32px 32px', pointerEvents:'none' }} />
      <div ref={ref} style={{ maxWidth:1280, margin:'0 auto', padding:'100px 24px' }}>
        <div style={{ textAlign:'center', marginBottom:72 }}>
          <span style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', color:lp.impactLabel, textTransform:'uppercase', display:'block', marginBottom:16, opacity:visible?1:0, transition:'opacity 0.6s ease' }}>Environmental Impact</span>
          <h2 style={{ ...TYPE.h2, color:lp.sectionHeading, margin:'0 0 16px', opacity:visible?1:0, transform:visible?'translateY(0)':'translateY(28px)', transition:'all 0.9s cubic-bezier(.22,1,.36,1) 0.1s' }}>
            Making a <em style={{ color:TD.lime }}>Real Difference</em>
          </h2>
          <p style={{ ...FF.body, fontSize:16, fontWeight:400, color:lp.impactText, maxWidth:520, margin:'0 auto', lineHeight:1.75 }}>Every report, every cleanup, every community action adds up to meaningful environmental change across Ghana.</p>
        </div>
        {/* ── Photo evidence grid ── */}
        {lbOpen && <Lightbox photos={DUMP_PHOTOS} index={lbIndex} onClose={lbClose} onPrev={lbPrev} onNext={lbNext} onGoTo={setLbIndex} />}
        <div style={{ marginBottom:56, opacity:visible?1:0, transform:visible?'translateY(0)':'translateY(32px)', transition:'all 0.9s cubic-bezier(.22,1,.36,1) 0.15s' }}>
          {/* Top row: tall left + 2×2 right */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gridTemplateRows:'180px 180px', gap:6, marginBottom:6 }}>
            {/* index 0 — tall left spanning 2 rows */}
            {[
              { style:{ gridRow:'1 / 3' }, idx:0 },
              { style:{},                   idx:1 },
              { style:{},                   idx:2 },
              { style:{},                   idx:3 },
              { style:{},                   idx:4 },
            ].map(({ style, idx }) => {
              const p = DUMP_PHOTOS[idx];
              return (
                <div key={idx} style={{ ...style, position:'relative', borderRadius:14, overflow:'hidden', cursor:'pointer' }}
                  onClick={() => setLbIndex(idx)}
                  onMouseEnter={e=>{e.currentTarget.querySelector('img').style.transform='scale(1.06)'; e.currentTarget.querySelector('.expand-icon').style.opacity='1';}}
                  onMouseLeave={e=>{e.currentTarget.querySelector('img').style.transform='scale(1)';    e.currentTarget.querySelector('.expand-icon').style.opacity='0';}}>
                  <img src={p.src} alt={dumpPhotoAlt(p)} style={{ width:'100%', height:'100%', objectFit:'cover', transition:'transform 0.55s cubic-bezier(.22,1,.36,1)' }} />
                  <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top,rgba(0,0,0,0.55) 0%,transparent 55%)', pointerEvents:'none' }} />
                  <span style={{ position:'absolute', bottom:10, left:12, ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', color:'rgba(255,255,255,0.85)', textTransform:'uppercase' }}>{p.label}</span>
                  <span className="expand-icon" style={{ position:'absolute', top:10, right:10, width:28, height:28, borderRadius:'50%', background:'rgba(0,0,0,0.45)', border:'1px solid rgba(255,255,255,0.2)', display:'flex', alignItems:'center', justifyContent:'center', opacity:0, transition:'opacity 0.2s', pointerEvents:'none' }}>
                    <i className="fas fa-expand-alt" style={{ fontSize:13, color:'#fff' }} />
                  </span>
                </div>
              );
            })}
          </div>
          {/* Bottom row: 4 equal images (indices 5-8) */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6 }}>
            {DUMP_PHOTOS.slice(5).map((img, i) => {
              const idx = i + 5;
              return (
                <div key={idx} style={{ position:'relative', height:130, borderRadius:12, overflow:'hidden', cursor:'pointer' }}
                  onClick={() => setLbIndex(idx)}
                  onMouseEnter={e=>{e.currentTarget.querySelector('img').style.transform='scale(1.07)'; e.currentTarget.querySelector('.expand-icon').style.opacity='1';}}
                  onMouseLeave={e=>{e.currentTarget.querySelector('img').style.transform='scale(1)';    e.currentTarget.querySelector('.expand-icon').style.opacity='0';}}>
                  <img src={img.src} alt={dumpPhotoAlt(img)} style={{ width:'100%', height:'100%', objectFit:'cover', transition:'transform 0.55s cubic-bezier(.22,1,.36,1)' }} />
                  <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top,rgba(0,0,0,0.6) 0%,transparent 60%)', pointerEvents:'none' }} />
                  <span style={{ position:'absolute', bottom:9, left:11, ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', color:'rgba(255,255,255,0.82)', textTransform:'uppercase' }}>{img.label}</span>
                  <span className="expand-icon" style={{ position:'absolute', top:8, right:8, width:24, height:24, borderRadius:'50%', background:'rgba(0,0,0,0.45)', border:'1px solid rgba(255,255,255,0.2)', display:'flex', alignItems:'center', justifyContent:'center', opacity:0, transition:'opacity 0.2s', pointerEvents:'none' }}>
                    <i className="fas fa-expand-alt" style={{ fontSize:13, color:'#fff' }} />
                  </span>
                </div>
              );
            })}
          </div>
          {/* Caption */}
          <p style={{ ...FF.label, fontSize:13, color: lp.impactSub, textAlign:'center', marginTop:14, letterSpacing:'0.08em', fontWeight:600 }}>
            Real reports submitted by TrashDrop users across Ghana · Photos used with permission
          </p>
        </div>

        {showNumbers && <div className="td-impact-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', borderTop:'1px solid rgba(182,238,105,0.1)', borderLeft:'1px solid rgba(182,238,105,0.1)' }}>
          {STATS.map((s,i) => (
            <div key={s.label} style={{ borderRight:'1px solid rgba(182,238,105,0.1)', borderBottom:'1px solid rgba(182,238,105,0.1)', opacity:visible?1:0, transform:visible?'translateY(0)':'translateY(32px)', transition:`all 0.8s cubic-bezier(.22,1,.36,1) ${i*0.1}s` }}>
              <ImpactStatCard {...s} ready={ready} />
            </div>
          ))}
        </div>}
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   PATHWAYS
   ═══════════════════════════════════════════════════════════════════════════════ */
const PathwaysSection = () => {
  const { ref, visible } = useReveal(0.08);
  const { lp } = useLpTheme();
  const PATHWAYS = [
    { title:'Local Authorities (MMDA)',     icon:'fa-landmark',     color:'#2563eb', bg:'rgba(77,157,224,0.15)', border:'rgba(77,157,224,0.25)', cta:'Register as Environmental Officer', link:'/signup?type=municipality', popular:true,
      features:['Monitor illegal dumping hotspots','Assign and dispatch cleanup teams','Track enforcement actions & outcomes','Generate compliance reports'] },
    { title:'Volunteers',                   icon:'fa-hands-helping', color:TD.lime,  bg:`${TD.lime}15`,           border:`${TD.lime}25`,           cta:'Volunteer with your group',          link:'/volunteers/',
      features:['Claim verified dumping reports in your region','Up to 3 open cleanups at a time','One shared login for the whole group','Before-and-after cleanup photos'] },
    { title:'Media & Journalists',          icon:'fa-newspaper',    color:'#e07a5f', bg:'rgba(224,122,95,0.15)', border:'rgba(224,122,95,0.25)', cta:'Get Media access',                  link:'/media/',
      features:['National view of reports and cleanups','No reporter details shared','Filter by date, region, district and status','CSV download, up to 5,000 reports'] },
    { title:'Waste Management Operators',   icon:'fa-truck',        color:TD.orange,   bg:`${TD.orange}15`,           border:`${TD.orange}25`,           cta:'Register as Waste Operator',         link:'/signup?type=waste_management',
      features:['Manage bins and collection points','Optimize waste collection routes','Track operational performance KPIs','Real-time fleet and schedule management'] },
  ];
  return (
    <section id="pathways" style={{ background:lp.sectionPathway, position:'relative', overflow:'hidden', padding:'var(--td-section-y) 0' }}>
      <div style={{ position:'absolute', top:-120, right:-120, width:500, height:500, borderRadius:'50%', background:`${TD.lime}08`, pointerEvents:'none', filter:'blur(80px)' }} />
      <div ref={ref} style={{ maxWidth:1280, margin:'0 auto', padding:'0 24px' }}>
        <div style={{ textAlign:'center', marginBottom:64 }}>
          <span style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', color:lp.pathwaysLabel, textTransform:'uppercase', display:'block', marginBottom:14, opacity:visible?1:0, transition:'opacity 0.6s ease' }}>Get Involved</span>
          <h2 style={{ ...TYPE.h2, color:lp.ink2Heading, margin:'0 0 16px', opacity:visible?1:0, transform:visible?'translateY(0)':'translateY(28px)', transition:'all 0.9s cubic-bezier(.22,1,.36,1) 0.1s' }}>
            Choose Your <em style={{ color:TD.forest }}>Pathway</em>
          </h2>
          <p style={{ ...FF.body, fontSize:16, fontWeight:400, color:lp.sageText, maxWidth:520, margin:'0 auto', lineHeight:1.75 }}>
            Whether you're a local authority, a volunteer group, a journalist or a waste operator — TrashDrop has tools designed specifically for you.
          </p>
        </div>
        <div className="td-pathways-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:20 }}>
          {PATHWAYS.map((p,i) => (
            <div key={p.title} className="td-pathway-card" style={{ position:'relative', background:lp.pathwayCardBg, border:`1.5px solid ${p.popular?p.color:'rgba(0,0,0,0.07)'}`, borderRadius:22, padding:'36px 24px', display:'flex', flexDirection:'column', boxShadow:p.popular?`0 20px 60px ${p.color}18`:'0 4px 24px rgba(0,0,0,0.05)', opacity:visible?1:0, transform:visible?'translateY(0)':'translateY(40px)', transition:`all 0.9s cubic-bezier(.22,1,.36,1) ${i*0.15}s`, cursor:'default' }}>
              {p.popular && <div style={{ position:'absolute', top:-14, left:'50%', transform:'translateX(-50%)', background:p.color, color:'#fff', ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'2px', padding:'6px 16px', borderRadius:99, whiteSpace:'nowrap', textTransform:'uppercase', boxShadow:`0 4px 16px ${p.color}60` }}>MOST POPULAR</div>}
              <div style={{ width:56, height:56, borderRadius:16, background:p.bg, border:`1px solid ${p.border}`, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:20 }}>
                <i className={`fas ${p.icon}`} style={{ fontSize:22, color:p.color }}></i>
              </div>
              <h3 style={{ ...TYPE.h3, color:lp.ink2Heading, margin:'0 0 20px' }}>{p.title}</h3>
              <ul style={{ margin:'0 0 28px', padding:0, listStyle:'none', display:'flex', flexDirection:'column', gap:10, flex:1 }}>
                {p.features.map(f => (
                  <li key={f} style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                    <i className="fas fa-check-circle" style={{ color:p.color, fontSize:14, marginTop:2, flexShrink:0 }}></i>
                    <span style={{ ...FF.body, fontSize:14, fontWeight:400, color:lp.pathwayText, lineHeight:1.6 }}>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to={p.link} style={{ display:'block', textAlign:'center', ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.5px', padding:14, borderRadius:12, textDecoration:'none', background:p.popular?p.color:'transparent', color:p.popular?(p.color===TD.lime?TD.ink:'#fff'):onLight(p.color), border:p.popular?'none':`1.5px solid ${p.color}`, boxShadow:p.popular?`0 6px 24px ${p.color}40`:'none', transition:'all 0.2s ease' }}>
                {p.cta}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   PARTNERSHIPS
   ═══════════════════════════════════════════════════════════════════════════════ */
const PartnershipsSection = () => {
  const { ref, visible } = useReveal(0.08);
  const { lp } = useLpTheme();
  const USE_CASES = [
    { title:'Smart Cities',              desc:"Integrate TrashDrop into your city's digital infrastructure for real-time waste intelligence.", icon:'fa-city',        color:'#60a5fa' },
    { title:'Municipal Waste Agencies',  desc:'Streamline operations, track compliance, and optimize resource allocation.',                   icon:'fa-building',    color:TD.lime   },
    { title:'Environmental NGOs',        desc:'Leverage data insights to drive advocacy and community-based cleanup programs.',               icon:'fa-globe-africa',color:'#34d399' },
    { title:'Recycling Companies',       desc:'Identify waste streams, optimize collection routes, and increase recycling rates.',            icon:'fa-recycle',     color:TD.orange   },
  ];
  return (
    <section id="partners" style={{ background:lp.sectionPartner, padding:'var(--td-section-y) 0' }}>
      <div ref={ref} style={{ maxWidth:1280, margin:'0 auto', padding:'0 24px' }}>
        <div className="td-partners-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:80, alignItems:'center' }}>
          <div>
            <span style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', color:TD.lime, textTransform:'uppercase', display:'block', marginBottom:16, opacity:visible?1:0, transition:'opacity 0.6s ease' }}>Partnerships</span>
            <h2 style={{ ...TYPE.h2, color:'#f0f5f0', margin:'0 0 20px', opacity:visible?1:0, transform:visible?'translateY(0)':'translateY(28px)', transition:'all 0.9s cubic-bezier(.22,1,.36,1) 0.1s' }}>
              Partner With <em style={{ color:TD.lime }}>TrashDrop</em>
            </h2>
            <p style={{ ...FF.body, fontSize:16, fontWeight:400, color:lp.partnerP, lineHeight:1.75, marginBottom:36 }}>
              Join a growing network of cities, agencies, and organizations using TrashDrop to build cleaner, smarter communities across Ghana and Africa.
            </p>
            <div style={{ display:'flex', flexWrap:'wrap', gap:12 }}>
              <Link to="/signup"
                style={{ display:'inline-flex', alignItems:'center', gap:8, background:TD.lime, color:TD.ink, ...FF.label, fontSize:13, fontWeight:700, padding:'14px 28px', borderRadius:10, textDecoration:'none', boxShadow:`0 8px 28px ${TD.lime}40`, transition:'all 0.2s ease' }}
                onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow=`0 14px 36px ${TD.lime}55`;}}
                onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow=`0 8px 28px ${TD.lime}40`;}}>
                <i className="fas fa-calendar-check"></i> Request Demo
              </Link>
              <a href="mailto:partnerships@trashdrops.com"
                style={{ display:'inline-flex', alignItems:'center', gap:8, background:'transparent', color:'rgba(255,255,255,0.75)', ...FF.label, fontSize:13, fontWeight:700, padding:'14px 28px', borderRadius:10, textDecoration:'none', border:'1.5px solid rgba(255,255,255,0.15)', transition:'all 0.2s ease' }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=`${TD.lime}50`;e.currentTarget.style.color=TD.lime;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor='rgba(255,255,255,0.15)';e.currentTarget.style.color='rgba(255,255,255,0.75)';}}>
                <i className="fas fa-envelope"></i> City Deployment
              </a>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
            {USE_CASES.map((uc,i) => (
              <div key={uc.title} className="td-partner-tile" style={{ background:lp.partnerTileBg, border:`1px solid ${lp.partnerTileB}`, borderRadius:16, padding:24, cursor:'pointer', opacity:visible?1:0, transform:visible?'translateY(0)':'translateY(32px)', transition:`all 0.8s cubic-bezier(.22,1,.36,1) ${0.2+i*0.1}s` }}>
                <div style={{ width:44, height:44, borderRadius:12, background:`${uc.color}15`, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:14 }}>
                  <i className={`fas ${uc.icon}`} style={{ color:uc.color, fontSize:18 }}></i>
                </div>
                <h4 style={{ ...FF.display, fontSize:18, color:lp.partnerH4, margin:'0 0 8px', letterSpacing:'-0.3px' }}>{uc.title}</h4>
                <p style={{ ...FF.body, fontSize:13, fontWeight:400, color:lp.partnerP, lineHeight:1.65, margin:0 }}>{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   SERVICE AREAS
   ═══════════════════════════════════════════════════════════════════════════════ */
// First few neighbourhoods from each city page, e.g. "East Legon, Madina, Adenta…"
const cityAreas = (slug) => `${CITY_CONTENT[slug].areas.slice(0, 3).join(', ')}…`;

const ServiceAreasSection = () => {
  const { lp } = useLpTheme();
  return (
    <section id="service-areas" style={{ background:lp.sectionAlt, padding:'var(--td-section-y) 0' }}>
      <div style={{ maxWidth:1280, margin:'0 auto' }}>
        <div style={{ padding:'0 24px', marginBottom:24 }}>
          <h2 style={{ ...TYPE.h2, color:lp.ink2Heading, margin:'0 0 8px' }}>
            Waste collection near you
          </h2>
          <p style={{ ...FF.body, fontSize:16, color:lp.sageText, margin:0 }}>Pick your city to see the areas we cover.</p>
        </div>
        <ul className="td-city-row" style={{ listStyle:'none', margin:0, padding:'4px 24px 16px', display:'grid', gridAutoFlow:'column', gridAutoColumns:'minmax(250px, 1fr)', gap:16, overflowX:'auto', scrollSnapType:'x mandatory', scrollPaddingInline:24, WebkitOverflowScrolling:'touch' }}>
          {CITY_PAGES.map(city => (
            <li key={city.slug} style={{ scrollSnapAlign:'start' }}>
              <Link to={city.path} style={{ display:'block', background:lp.cardBg, border:`1px solid ${lp.cardBorder}`, borderRadius:16, overflow:'hidden', textDecoration:'none', height:'100%' }}>
                {CITY_CARD_PHOTOS[city.slug]
                  ? <img src={CITY_CARD_PHOTOS[city.slug]} alt={`TrashDrop waste pickup in ${city.name}`} width="640" height="420" loading="lazy" decoding="async"
                      style={{ display:'block', width:'100%', height:'auto', aspectRatio:'640 / 420', objectFit:'cover', background:'#e5e7eb' }} />
                  : <div aria-hidden="true" style={{ aspectRatio:'640 / 420', background:`linear-gradient(135deg, ${TD.forest}, #8FC638)`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <span style={{ ...FF.display, fontSize:30, color:'#fff' }}><i className="fas fa-map-marker-alt" style={{ fontSize:22, marginRight:10 }}></i>{city.name}</span>
                    </div>}
                <div style={{ padding:'14px 16px 16px' }}>
                  <h3 style={{ ...TYPE.card, color:lp.ink2Heading, margin:'0 0 4px' }}>Waste collection in {city.name}</h3>
                  <p style={{ ...FF.body, fontSize:14, color:lp.sageText, margin:0, lineHeight:1.5 }}>{cityAreas(city.slug)}</p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   FOOTER
   ═══════════════════════════════════════════════════════════════════════════════ */
const HomeFooter = () => {
  const { lp, lpMode, toggleLp } = useLpTheme();
  const isDark = lpMode === 'dark';
  return (
  <footer style={{ background:lp.footerBg, borderTop:`1px solid ${lp.footerBorder}` }}>
    <div style={{ maxWidth:1280, margin:'0 auto', padding:'72px 24px 32px' }}>
      <div className="td-footer-grid" style={{ display:'grid', gridTemplateColumns:'1.8fr 1fr 1fr 1fr', gap:48, marginBottom:64 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
            <img src="/logo.svg" alt="TrashDrop" style={{ height:40, width:40 }} />
            <span style={{ ...FF.label, fontSize:20, fontWeight:700, color:'#fff', letterSpacing:'-0.3px' }}>Trash<span style={{ color:TD.lime }}>Drop</span></span>
          </div>
          <p style={{ ...FF.body, fontSize:14, fontWeight:400, color:TD.sageOnDark, lineHeight:1.75, maxWidth:280, marginBottom:24 }}>
            Trash and waste collection in Accra, Kumasi, Takoradi and Tamale, plus illegal dumping reporting across Ghana. Call or WhatsApp {BUSINESS.phoneDisplay}.
          </p>
          <div style={{ display:'flex', gap:10 }}>
            {[['fab fa-twitter','#'],['fab fa-facebook-f',BUSINESS.facebookUrl],['fab fa-linkedin-in','#'],['fab fa-instagram','#']].map(([ic,href]) => (
              <a key={ic} href={href} className="td-social-btn" aria-label={SOCIAL_NAMES[ic]} style={{ width:36, height:36, borderRadius:8, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', display:'flex', alignItems:'center', justifyContent:'center', color:TD.sageOnDark, textDecoration:'none', transition:'all 0.2s ease', fontSize:13 }}>
                <i className={ic} aria-hidden="true"></i>
              </a>
            ))}
          </div>
        </div>
        {[
          { title:'Waste Collection', links:[...CITY_PAGES.map(c => [c.name, c.path]), ['On-demand waste pickup', WASTE_PICKUP_PATH]] },
          { title:'Platform', links:[['How It Works','/how-it-works/'],['FAQ','/faq/'],['For Households','/users/'],['Earn as Carter','/collectors/'],['Report Dumping','/illegal-dumping/']] },
          { title:'Company',  links:[['About Us','/about/'],['Blog','/blog/'],['Partners','/partners/'],['WhatsApp Us',BUSINESS.whatsappUrl],['Contact Us','mailto:hello@trashdrops.com']] },
        ].map(col => (
          <div key={col.title}>
            <h4 style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:TD.sageOnDark, marginBottom:20 }}>{col.title}</h4>
            {col.links.map(([label,href]) => (
              href.startsWith('/') || href.startsWith('#')
                ? <Link key={label} to={href.startsWith('/')? href : '#'} className="td-footer-link" style={{ display:'block', ...FF.body, fontSize:14, fontWeight:400, color:TD.sageOnDark, textDecoration:'none', marginBottom:10, transition:'color 0.2s' }}>{label}</Link>
                : <a key={label} href={href} className="td-footer-link" style={{ display:'block', ...FF.body, fontSize:14, fontWeight:400, color:TD.sageOnDark, textDecoration:'none', marginBottom:10, transition:'color 0.2s' }}>{label}</a>
            ))}
          </div>
        ))}
      </div>
      <div style={{ borderTop:'1px solid rgba(255,255,255,0.06)', paddingTop:28, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
        <p style={{ ...FF.body, fontSize:13, fontWeight:400, color:TD.sageOnDark, margin:0 }}>&copy; {new Date().getFullYear()} TrashDrop by Infobrix Limited. All rights reserved.</p>
        {/* Legal pages (Google's sign-in verification looks for them on the homepage) */}
        <nav aria-label="Legal" style={{ display:'flex', gap:20, flexWrap:'wrap' }}>
          {[['Privacy Policy','/privacy/'],['Terms of Service','/terms/']].map(([label, href]) => (
            <Link key={href} to={href} className="td-footer-link" style={{ ...FF.body, fontSize:13, color:TD.sageOnDark, textDecoration:'none' }}>{label}</Link>
          ))}
        </nav>
        {/* The light/dark switch lives here, out of the header */}
        <button type="button" onClick={toggleLp} className="td-lp-toggle" aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{ display:'inline-flex', alignItems:'center', gap:8, minHeight:40, padding:'0 16px', borderRadius:999, background:'transparent', color:TD.sageOnDark, border:'1px solid rgba(255,255,255,0.24)', ...TYPE.small, fontWeight:600, cursor:'pointer' }}>
          <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`} aria-hidden="true"></i> {isDark ? 'Light mode' : 'Dark mode'}
        </button>
      </div>
    </div>
  </footer>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   APP DOWNLOAD BANNER
   ═══════════════════════════════════════════════════════════════════════════════ */
const CollectorsLine = () => {
  const { lp } = useLpTheme();
  return (
    <section style={{ background:lp.pageBg, padding:'var(--td-section-y-compact) 24px' }}>
      <p style={{ ...FF.body, fontSize:18, lineHeight:1.6, color:lp.ink2Heading, textAlign:'center', maxWidth:720, margin:'0 auto' }}>
        We work with existing collectors and aboboyaa crews, giving them better tools and steady jobs.
      </p>
    </section>
  );
};

const AppDownloadBanner = () => {
  const { lpMode } = useLpTheme();
  const isDark = lpMode === 'dark';
  return (
    <section style={{ background: isDark ? '#0a140a' : TD.cream, padding:'var(--td-section-y) 24px' }}>
      <div style={{
        maxWidth: 960,
        margin: '0 auto',
        background: TD.lime,
        borderRadius: 24,
        padding: '52px 56px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 40,
        position: 'relative',
        overflow: 'hidden',
        flexWrap: 'wrap',

      }}>

        {/* Left: text */}
        <div style={{ position:'relative', zIndex:1, flex:1, minWidth:260 }}>
          <span style={{ ...TYPE.eyebrow, color:TD.ink, display:'block', marginBottom:12 }}>Get started today — it&rsquo;s free</span>
          <h2 style={{ ...TYPE.h2, color:TD.ink, margin:'0 0 16px' }}>
            Book your first pickup.<br />Or report an illegal dump.
          </h2>
          <p className="td-app-qr" style={{ ...TYPE.body, color:TD.ink, margin:0 }}>Scan the code to download the TrashDrop app.</p>
          {/* Phones open the app instead of scanning their own screen */}
          <a href={HOUSEHOLD_APP_URL} className="td-app-button" style={{ ...BTN.primary, background:TD.ink, display:'none', marginTop:8 }}>
            <i className="fas fa-mobile-alt" aria-hidden="true"></i> Open the TrashDrop app
          </a>
        </div>

        {/* Right: QR code */}
        <div id="download-app" className="td-app-qr" data-scroll-align="center" style={{ position:'relative', zIndex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:12, flexShrink:0 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:12, boxShadow:'0 8px 32px rgba(0,0,0,0.18)' }}>
            <img
              src="/images/book-in-app-qr.png"
              alt="Scan to download TrashDrop"
              style={{ width:148, height:148, display:'block', borderRadius:8 }}
            />
          </div>
          <span style={{ ...TYPE.eyebrow, color:TD.ink }}>Scan to download</span>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   FLOATING FAB
   ═══════════════════════════════════════════════════════════════════════════════ */
// Fixed, so it never moves the page. Shows once the top booking card has scrolled out of view.
const StickyBookButton = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const card = document.getElementById('book');
    if (!card || !('IntersectionObserver' in window)) return undefined;
    const obs = new IntersectionObserver(([e]) => setShow(!e.isIntersecting));
    obs.observe(card);
    return () => obs.disconnect();
  }, []);
  // Booking happens in the app: go back to the booking card
  const goToCard = (e) => {
    e.preventDefault();
    document.getElementById('book-app').scrollIntoView({ behavior:'smooth', block:'center' });
  };
  return (
    <div className="td-sticky-book" data-testid="sticky-book" aria-hidden={!show}
      style={{ position:'fixed', zIndex:2000, transition:'transform 0.25s ease, opacity 0.25s ease', transform: show ? 'none' : 'translateY(140%)', opacity: show ? 1 : 0, pointerEvents: show ? 'auto' : 'none' }}>
      <a href="#book-app" onClick={goToCard} tabIndex={show ? 0 : -1}
        style={{ ...BTN.primary, display:'flex', minHeight:52, padding:'0 26px', boxShadow:'0 8px 28px rgba(0,0,0,0.28)' }}>
        <i className="fas fa-truck" aria-hidden="true"></i> Book a pickup
      </a>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   PRICING  (kept from existing, restyled)
   ═══════════════════════════════════════════════════════════════════════════════ */
const PricingGuideSection = () => {
  const { ref, visible } = useReveal(0.05);
  const { lp } = useLpTheme();
  const PLANS = [
    { type:'Recyclable', color:'#4d9de0', bg:'#1a2a3a', badge:'LOWEST PRICE · BEST VALUE', desc:'Plastics, paper, metals. Collector earns from material resale.',
      prices:[{l:'Small',s:'90L',p:'₵115'},{l:'Medium',s:'120L',p:'₵140'},{l:'Large',s:'240L',p:'₵170'}] },
    { type:'General',    color:'#f0a830', bg:'#2a2016', badge:'MOST FLEXIBLE',              desc:'Everyday mixed waste. Standard collection, no sorting required.',
      prices:[{l:'Small',s:'90L',p:'₵140'},{l:'Medium',s:'120L',p:'₵165'},{l:'Large',s:'240L',p:'₵195'}] },
    { type:'Organic',    color:TD.lime,   bg:'#1a2a16', badge:'ECO-PREMIUM',                desc:'Food scraps, garden waste. Composting-ready handling.',
      prices:[{l:'Small',s:'90L',p:'₵160'},{l:'Medium',s:'120L',p:'₵195'},{l:'Large',s:'240L',p:'₵225'}] },
  ];
  const SUBS = [
    { pct:'5%',  tier:'BASIC',     items:['1 batch/month','Any type or size','Cancel anytime'],              ex:'e.g. Recyclable Small — ₵109/mo' },
    { pct:'10%', tier:'REGULAR',   items:['2 batches/month','Mix & match type and size'],                    ex:'e.g. 2× General Medium — ₵297/mo', popular:true },
    { pct:'15%', tier:'HOUSEHOLD', items:['6 batches','Quarterly prepay','Full mix & match'],                ex:'e.g. 6× Recyclable Small — ₵586/qtr' },
  ];
  return (
    <section id="pricing" style={{ background:lp.sectionPricing, scrollMarginTop:132 }}>
      <div style={{ position:'relative', overflow:'hidden', minHeight:340, display:'flex', alignItems:'center', background:'#0a1a0a' }}>
        {/* 3-image row — right 50% of the banner, full height */}
        <div style={{ position:'absolute', top:0, right:0, height:'100%', display:'flex', alignItems:'stretch', gap:2, zIndex:1 }}>
          <img src="/images/home/pricing-bags-1.jpg" width="1054" height="700" loading="lazy" decoding="async" alt="TrashDrop QR-coded waste bags for organic, general and recyclable waste"     style={{ height:'100%', width:'auto', display:'block', opacity:0.85 }} />
          <img src="/images/home/pricing-bags-2.jpg" width="1086" height="578" loading="lazy" decoding="async" alt="QR code label on a TrashDrop waste collection bag"     style={{ height:'100%', width:'auto', display:'block' }} />
        </div>
        {/* Dark gradient — solid left, barely touches the image area on the right */}
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(90deg, #0a1a0a 0%, #0a1a0a 40%, rgba(10,26,10,0.7) 52%, rgba(10,26,10,0.08) 68%, transparent 100%)', zIndex:2 }} />
        {/* Subtle lime grid overlay */}
        <div style={{ position:'absolute', inset:0, zIndex:2, opacity:0.03, backgroundImage:`linear-gradient(rgba(182,238,105,0.6) 1px,transparent 1px),linear-gradient(90deg,rgba(182,238,105,0.6) 1px,transparent 1px)`, backgroundSize:'40px 40px' }} />
        {/* Lime left-edge accent line */}
        <div style={{ position:'absolute', top:0, left:0, width:3, height:'100%', background:`linear-gradient(to bottom,transparent,${TD.lime},transparent)`, zIndex:3 }} />
        <div style={{ position:'absolute', top:16, right:16, background:TD.lime, color:TD.ink, ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', padding:'6px 14px', borderRadius:4, zIndex:4 }}>2026 PRICING</div>
        <div style={{ position:'relative', zIndex:3, padding:'60px 40px', maxWidth:'50%' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
            <div style={{ width:28, height:2, background:TD.lime }} />
            <span style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'5px', color:TD.lime, textTransform:'uppercase' }}>Smart Waste</span>
          </div>
          <h2 style={{ ...FF.data, fontSize:'clamp(52px,7vw,88px)', color:'#fff', lineHeight:0.9, letterSpacing:'2px', margin:0 }}>SMART WASTE.<br /><span style={{ color:TD.lime }}>FAIR PRICE.</span></h2>
          <p style={{ ...FF.body, fontSize:14, color:'rgba(255,255,255,0.68)', marginTop:16, lineHeight:1.6 }}>QR-tagged bags · on-demand pickup · no hidden fees<br />Prices from ₵115/batch · Free collection on all plans</p>
        </div>
      </div>
      <div style={{ background:TD.lime, textAlign:'center', ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', color:TD.ink, padding:12, textTransform:'uppercase' }}>+ Collection is FREE on every bag in the batch</div>
      <div ref={ref} style={{ padding:'48px 40px 0' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:28 }}>
          <div style={{ width:36, height:2, background:TD.lime }} />
          <span style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', color:lp.subTierLabel, textTransform:'uppercase' }}>Bag Pricing — Per batch of 5 bags</span>
        </div>
        <div className="td-pricing-grid" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
          {PLANS.map((p,i) => (
            <div key={p.type} style={{ background:lp.subBg, border:`1px solid ${lp.sectionPricing==='#fafafa'?'rgba(0,0,0,0.08)':'rgba(255,255,255,0.07)'}`, borderRadius:10, padding:24, opacity:visible?1:0, transform:visible?'translateY(0)':'translateY(32px)', transition:`all 0.8s cubic-bezier(.22,1,.36,1) ${i*0.1}s` }}>
              <h3 style={{ ...FF.data, fontSize:32, color:p.color, letterSpacing:'2px', margin:'0 0 4px' }}>{p.type}</h3>
              <p style={{ ...FF.body, fontSize:13, color:lp.pricingDesc, lineHeight:1.5, margin:'0 0 8px' }}>{p.desc}</p>
              <span style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'1.5px', background:`${p.color}20`, color:p.color, borderRadius:3, padding:'3px 8px', textTransform:'uppercase' }}>{p.badge}</span>
              <div style={{ marginTop:16 }}>
                {p.prices.map(pr => (
                  <div key={pr.l} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderTop:`1px solid ${lp.pricingRowBorder}` }}>
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      <span style={{ ...FF.label, fontSize:13, fontWeight:700, background:`${p.color}25`, color:p.color, borderRadius:3, padding:'2px 7px' }}>{pr.l}</span>
                      <span style={{ ...FF.label, fontSize:13, color:lp.pricingSize, fontWeight:600 }}>{pr.s}</span>
                    </div>
                    <span style={{ ...FF.data, fontSize:28, color:lp.mapStatText, letterSpacing:'1px' }}>{pr.p}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ padding:'36px 40px 0' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
          <div style={{ width:36, height:2, background:TD.lime }} />
          <span style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', color:lp.subTierLabel, textTransform:'uppercase' }}>Monthly Subscription — Save up to 15%</span>
        </div>
        <div className="td-pricing-grid" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
          {SUBS.map(s => (
            <div key={s.tier} style={{ position:'relative', background:lp.subBg, border:`1px solid ${s.popular?`${TD.lime}35`:lp.pricingRowBorder}`, borderRadius:10, padding:24 }}>
              {s.popular && <span style={{ position:'absolute', top:-12, left:'50%', transform:'translateX(-50%)', background:TD.lime, color:TD.ink, ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', padding:'4px 12px', borderRadius:3, whiteSpace:'nowrap', textTransform:'uppercase' }}>Most Popular</span>}
              <div style={{ ...FF.data, fontSize:52, color:TD.lime, lineHeight:1, marginBottom:4, letterSpacing:'2px' }}>{s.pct}</div>
              <div style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', color:lp.subTierLabel, textTransform:'uppercase', marginBottom:16 }}>{s.tier}</div>
              <div style={{ marginBottom:16 }}>
                {s.items.map(it => <p key={it} style={{ ...FF.body, fontSize:13, color:lp.subText, margin:'4px 0', lineHeight:1.5 }}>· {it}</p>)}
              </div>
              <p style={{ ...FF.body, fontSize:13, color:lp.subSmall, borderTop:`1px solid ${lp.sectionPricing==='#fafafa'?'rgba(0,0,0,0.08)':'rgba(255,255,255,0.07)'}`, paddingTop:12, margin:0 }}>{s.ex}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   MAIN PAGE COMPONENT
   ═══════════════════════════════════════════════════════════════════════════════ */
const HomePageNew = () => {
  const { isAuthenticated, authInitialized } = useAuth();
  const auth = authInitialized ? isAuthenticated : false;
  return (
    <LpThemeProvider>
      <LpPageInner auth={auth} />
    </LpThemeProvider>
  );
};

export const HOME_SEO = {
  title: 'Waste Collection & Trash Pickup in Accra, Kumasi, Takoradi & Tamale | TrashDrop',
  description: 'Book waste collection and trash pickup in Accra, Kumasi, Takoradi & Tamale. Free pickup with QR Bags, on-demand bins, live tracking. Pay MoMo or cash.',
  path: '/',
};

const LpPageInner = ({ auth }) => {
  const { lp } = useLpTheme();
  useSeo({ ...HOME_SEO, jsonLd: [organizationJsonLd(), localBusinessJsonLd()] });
  // One load for the map, its stats and the impact numbers, so they always agree
  const dumpingData = usePublicDumpingReports();
  return (
    <div className="td-page" style={{ minHeight:'100vh', background:lp.pageBg, overflowX:'hidden', ...FF.body, transition:'background 0.3s ease' }}>
      <GlobalStyles />
      <HomeNavbar         isAuthenticated={auth} />
      <HeroSection />
      <TrustRow />
      <ServicesSection />
      <TrustSection />
      <MapPreviewSection  isAuthenticated={auth} dumpingData={dumpingData} applyThresholds home />
      <ServiceAreasSection />
      <CollectorsLine />
      <AppDownloadBanner />
      <HomeFooter />
      <StickyBookButton />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   /partners — assemblies, operators, businesses and community groups
   ═══════════════════════════════════════════════════════════════════════════════ */
export const PARTNERS_SEO = {
  title: 'Partner with TrashDrop | Assemblies, Operators & Businesses in Ghana',
  description: 'For MMDAs, waste operators and businesses in Ghana: live illegal dumping map, hotspot and risk data, cleanup dispatch and municipal dashboards.',
  path: '/partners/',
};

const PartnersPageInner = ({ auth }) => {
  const { lp } = useLpTheme();
  useSeo({ ...PARTNERS_SEO, jsonLd: [organizationJsonLd()] });
  // One load for the map, its stats and the impact numbers, so they always agree
  const dumpingData = usePublicDumpingReports();
  return (
    <div className="td-page td-partners-page" style={{ minHeight:'100vh', background:lp.pageBg, overflowX:'hidden', ...FF.body }}>
      <GlobalStyles />
      <HomeNavbar isAuthenticated={auth} />
      <header style={{ background:TD.ink, padding:'136px 24px 64px' }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          <span style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', color:TD.lime, textTransform:'uppercase' }}>Partners</span>
          <h1 style={{ ...FF.display, fontSize:'clamp(36px,4.6vw,58px)', lineHeight:1.05, letterSpacing:'-1.5px', color:'#f0f5f0', margin:'12px 0 14px' }}>
            TrashDrop for assemblies, operators and businesses
          </h1>
          <p style={{ ...FF.body, fontSize:18, lineHeight:1.6, color:'rgba(255,255,255,0.75)', maxWidth:640, margin:0 }}>
            Live illegal dumping reports, hotspot and risk data, and cleanup crews you can dispatch, across Ghana.
          </p>
        </div>
      </header>
      <section aria-label="Our call to action" style={{ background:TD.lime, padding:'var(--td-section-y) 24px' }}>
        <div className="td-trashwalk" style={{ maxWidth:1100, margin:'0 auto', display:'grid', gridTemplateColumns:'0.8fr 1.2fr', alignItems:'center', gap:48 }}>
          <img src="/images/partners/trashwalk.jpg" width="720" height="770" loading="lazy" decoding="async"
            alt="A resident on a trash walk holds up the TrashDrop app, with map pins over dump sites along the street"
            style={{ width:'100%', height:'auto', aspectRatio:'720 / 770', objectFit:'cover', borderRadius:20, boxShadow:'0 24px 60px rgba(10,15,10,0.35)', border:`3px solid ${TD.ink}`, display:'block' }} />
          <div>
            <span style={{ ...FF.label, fontSize:13, fontWeight:700, letterSpacing:'0.08em', color:TD.forestDk, textTransform:'uppercase', display:'block', marginBottom:14 }}>Trashwalk</span>
            <p style={{ ...FF.display, fontStyle:'italic', fontSize:'clamp(30px,3.8vw,50px)', lineHeight:1.08, letterSpacing:'-0.8px', color:TD.ink, margin:'0 0 28px' }}>
              Trash walk is a health walk — one photo, cleaner community.
            </p>
            <Link to="/illegal-dumping/" style={{ display:'inline-flex', alignItems:'center', gap:8, background:TD.ink, color:TD.lime, ...FF.label, fontSize:14, fontWeight:700, padding:'14px 24px', borderRadius:10, textDecoration:'none', whiteSpace:'nowrap' }}>
              <i className="fas fa-camera" aria-hidden="true"></i> Report illegal dumping
            </Link>
          </div>
        </div>
      </section>
      <PathwaysSection />
      <MapPreviewSection  isAuthenticated={auth} dumpingData={dumpingData} />
      <ImpactSection      dumpingData={dumpingData} />
      <PartnershipsSection />
      <HomeFooter />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   /how-it-works — the household sections: steps and bag pricing (the two services stay on the homepage)
   ═══════════════════════════════════════════════════════════════════════════════ */
// Bag pricing for /how-it-works/ (its steps are on that page already)
export const HouseholdPricing = () => (
  <LpThemeProvider mode="dark">
    <GlobalStyles />
    <PricingGuideSection />
  </LpThemeProvider>
);

export const PartnersPage = () => {
  const { isAuthenticated, authInitialized } = useAuth();
  return (
    <LpThemeProvider>
      <PartnersPageInner auth={authInitialized ? isAuthenticated : false} />
    </LpThemeProvider>
  );
};

export default HomePageNew;
